import { useState, useEffect, useRef, useCallback } from 'react';
import * as Ably from 'ably';
import { LITE } from './lite';

const API      = process.env.REACT_APP_API_URL || '/api';
const ABLY_AUTH_URL = (process.env.REACT_APP_API_URL || '/api') + '/ably/token';

// ─── CSS ──────────────────────────────────────────────────────────────────────
const STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { overflow: hidden; touch-action: none; -webkit-tap-highlight-color: transparent; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,180,0,.3); border-radius: 2px; }

  @keyframes liveFlash    { 0%,100%{opacity:1} 50%{opacity:.1} }
  @keyframes fadeInUp     { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn       { from{opacity:0} to{opacity:1} }
  @keyframes fadeOut      { from{opacity:1} to{opacity:0} }
  @keyframes popBounce    {
    0%{transform:translate(-50%,-50%) scale(0);opacity:0}
    60%{transform:translate(-50%,-50%) scale(1.22);opacity:1}
    80%{transform:translate(-50%,-50%) scale(0.94)}
    100%{transform:translate(-50%,-50%) scale(1)}
  }
  @keyframes wordReveal   {
    0%{opacity:0;transform:translate(-50%,-50%) scale(0.3) rotate(-6deg);}
    55%{opacity:1;transform:translate(-50%,-50%) scale(1.18) rotate(1deg);}
    100%{transform:translate(-50%,-50%) scale(1) rotate(0deg);}
  }
  @keyframes shimmer      { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
  @keyframes orb1         { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(40px,-30px) scale(1.12)} }
  @keyframes orb2         { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-35px,25px) scale(0.9)} }
  @keyframes orb3         { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(20px,40px) scale(1.08)} }
  @keyframes pulseGlow    { 0%,100%{box-shadow:0 0 30px rgba(255,180,0,.3)} 50%{box-shadow:0 0 60px rgba(255,180,0,.7),0 0 120px rgba(255,100,0,.3)} }
  @keyframes slideInRight { from{transform:translateX(32px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes scoreCount   { from{transform:scale(1.4);color:#fff} to{transform:scale(1)} }
  @keyframes promptReveal {
    0%{clip-path:inset(0 100% 0 0);opacity:0}
    100%{clip-path:inset(0 0% 0 0);opacity:1}
  }
  @keyframes circleBreath {
    0%,100%{filter:drop-shadow(0 30px 80px rgba(0,0,0,.7)) drop-shadow(0 0 100px rgba(30,140,200,.12))}
    50%{filter:drop-shadow(0 40px 100px rgba(0,0,0,.8)) drop-shadow(0 0 160px rgba(30,140,200,.22))}
  }
  @keyframes scanline     { from{top:-4px} to{top:100%} }
  @keyframes rankBadge    { from{transform:scale(0) rotate(-20deg);opacity:0} to{transform:scale(1) rotate(0deg);opacity:1} }
  @keyframes timerPulse   { 0%,100%{opacity:1} 50%{opacity:.55} }
  @keyframes advanceTick  { 0%{transform:scale(1.45);opacity:.35} 100%{transform:scale(1);opacity:1} }

  /* ── Animated background ── */
  @keyframes bgOrb1 { 0%,100%{transform:translate(0,0) scale(1);opacity:.7} 33%{transform:translate(60px,-40px) scale(1.2);opacity:1} 66%{transform:translate(-30px,60px) scale(0.9);opacity:.8} }
  @keyframes bgOrb2 { 0%,100%{transform:translate(0,0) scale(1);opacity:.6} 40%{transform:translate(-70px,30px) scale(1.15);opacity:.9} 70%{transform:translate(40px,-50px) scale(0.85);opacity:.7} }
  @keyframes bgOrb3 { 0%,100%{transform:translate(0,0) scale(1);opacity:.5} 50%{transform:translate(50px,60px) scale(1.1);opacity:.8} }
  @keyframes bgOrb4 { 0%,100%{transform:translate(0,0) scale(1)} 60%{transform:translate(-40px,-30px) scale(1.18)} }
  @keyframes starTwinkle { 0%,100%{opacity:.2;transform:scale(1)} 50%{opacity:1;transform:scale(1.5)} }
  @keyframes streamLine { from{transform:translateX(-100%)} to{transform:translateX(100vw)} }
  @keyframes auroraPulse { 0%,100%{opacity:.15;transform:scaleX(1)} 50%{opacity:.3;transform:scaleX(1.08)} }

  /* ── Question intro animations ── */
  @keyframes introBgPulse {
    0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%}
  }
  @keyframes introSlideDown {
    0%{opacity:0;transform:translateY(-60px) scaleY(0.6)}
    70%{opacity:1;transform:translateY(6px) scaleY(1.02)}
    100%{opacity:1;transform:translateY(0) scaleY(1)}
  }
  @keyframes introTextReveal {
    0%{opacity:0;transform:translateY(30px);letter-spacing:0.3em}
    60%{opacity:1;transform:translateY(-4px)}
    100%{opacity:1;transform:translateY(0);letter-spacing:inherit}
  }
  @keyframes introBannerSlide {
    0%{opacity:0;transform:translateX(-100%)}
    60%{opacity:1;transform:translateX(3%)}
    100%{opacity:1;transform:translateX(0)}
  }
  @keyframes introCountdown {
    0%{transform:scale(1.5);opacity:0}
    30%{transform:scale(1);opacity:1}
    80%{transform:scale(1);opacity:1}
    100%{transform:scale(0.8);opacity:0}
  }

  /* ── Enhanced transition: question overlay -> game board ── */
  @keyframes overlayEnter {
    0%{opacity:0;transform:scale(1.06);filter:blur(8px)}
    60%{opacity:1;filter:blur(2px)}
    100%{opacity:1;transform:scale(1);filter:blur(0)}
  }
  @keyframes gameReveal {
    0%{opacity:0;transform:scale(1.06) translateY(16px);filter:blur(10px)}
    45%{opacity:.7;filter:blur(3px)}
    100%{opacity:1;transform:scale(1) translateY(0);filter:blur(0)}
  }
  @keyframes overlayExit {
    0%{opacity:1;transform:scale(1);filter:blur(0)}
    40%{transform:scale(1.03);filter:blur(3px)}
    100%{opacity:0;transform:scale(1.1) translateY(-24px);filter:blur(14px)}
  }
  @keyframes shockwaveRing {
    0%{transform:translate(-50%,-50%) scale(0);opacity:.9;border-width:8px}
    100%{transform:translate(-50%,-50%) scale(3.5);opacity:0;border-width:1px}
  }
  @keyframes flashBurst {
    0%{opacity:0} 15%{opacity:1} 35%{opacity:.6} 60%{opacity:.2} 100%{opacity:0}
  }

  /* ── Winner name pop ── */
  @keyframes winnerPop {
    0%{transform:scale(1)}
    20%{transform:scale(1.55);filter:drop-shadow(0 0 30px #FFD700) drop-shadow(0 0 60px rgba(255,215,0,.7));color:#FFD700}
    50%{transform:scale(1.35);filter:drop-shadow(0 0 20px #FFB300)}
    70%{transform:scale(1.1)}
    100%{transform:scale(1);filter:none}
  }
  @keyframes winnerGlow {
    0%,100%{text-shadow:0 0 20px rgba(255,215,0,.8)}
    50%{text-shadow:0 0 60px rgba(255,215,0,1),0 0 100px rgba(255,140,0,.6),0 0 140px rgba(255,80,0,.3)}
  }

  /* ── Classic TV Show board ── */
  @keyframes studioSweep   { from{left:-40%} to{left:120%} }
  @keyframes studioGlow    { 0%,100%{opacity:.10} 50%{opacity:.26} }
  @keyframes classicReveal {
    0%{opacity:0;transform:translateX(-22px) scale(.97)}
    60%{transform:translateX(3px) scale(1.01)}
    100%{opacity:1;transform:translateX(0) scale(1)}
  }

  /* ── Scatter board ──
     IMPORTANT: No translate(-50%,-50%) inside keyframes — the wrapper div
     uses transform:translate(-50%,-50%) as its base position. Animations
     must only add incremental transforms via translateY/scale/rotate so the
     base centering is never overridden during playback.
  */
  @keyframes scatterWordIn {
    0%  { opacity:0; transform:translate(-50%,-50%) scale(0.2) rotate(-10deg); filter:blur(10px); }
    60% { opacity:1; transform:translate(-50%,-50%) scale(1.12) rotate(1.5deg); filter:blur(0); }
    80% { transform:translate(-50%,-50%) scale(0.95) rotate(-0.5deg); }
    100%{ opacity:1; transform:translate(-50%,-50%) scale(1) rotate(0deg); filter:blur(0); }
  }
  @keyframes scatterRevealBurst {
    0%  { filter:brightness(1)  drop-shadow(0 0 0px rgba(255,215,0,0)); }
    25% { filter:brightness(2.8) drop-shadow(0 0 40px rgba(255,215,0,1)); }
    60% { filter:brightness(1.4) drop-shadow(0 0 20px rgba(255,180,0,.6)); }
    100%{ filter:brightness(1)  drop-shadow(0 0 8px  rgba(255,180,0,.3)); }
  }
  @keyframes scatterBobA {
    0%,100%{ transform:translate(-50%,-50%) translateY(0px); }
    50%    { transform:translate(-50%,-50%) translateY(-8px); }
  }
  @keyframes scatterBobB {
    0%,100%{ transform:translate(-50%,-50%) translateY(0px); }
    50%    { transform:translate(-50%,-50%) translateY(-6px); }
  }
  @keyframes scatterBobC {
    0%,100%{ transform:translate(-50%,-50%) translateY(0px) scale(1); }
    50%    { transform:translate(-50%,-50%) translateY(-11px) scale(1.015); }
  }
  @keyframes scatterGoldPulse {
    0%,100%{ text-shadow:0 0 20px rgba(255,210,0,.6), 0 0 45px rgba(255,150,0,.35), 0 2px 4px rgba(0,0,0,.9); }
    50%    { text-shadow:0 0 40px rgba(255,220,0,1),  0 0 90px rgba(255,160,0,.65), 0 0 140px rgba(255,100,0,.2), 0 2px 4px rgba(0,0,0,.9); }
  }
  @keyframes scatterDimFloat {
    0%,100%{ opacity:.7; }
    50%    { opacity:.5; }
  }
  @keyframes scatterBannerIn {
    0%  { opacity:0; transform:translateY(-18px); filter:blur(4px); }
    100%{ opacity:1; transform:translateY(0);     filter:blur(0); }
  }
  @keyframes scatterStatusIn {
    0%  { opacity:0; transform:translateY(12px); }
    100%{ opacity:1; transform:translateY(0); }
  }
`;


// ─── Audio ────────────────────────────────────────────────────────────────────
// All sounds are pre-encoded MP3/OGG files served from /sounds/.
// No Web Audio API, no AudioContext lifecycle issues.
//
// Sounds:
//   sounds.correct()           — upward arpeggio when a player reveals an answer
//   sounds.incorrect()         — buzzer for wrong answers
//   sounds.questionComplete()  — triumphant 4-chord stab when all answers found
//   sounds.gameOver()          — big finale cascade + long chord hold
//   sounds.startMusic()        — begin looping TV gameshow background track
//   sounds.stopMusic()         — fade out and pause background music

function useSound() {
  const correctRef          = useRef(null);
  const incorrectRef        = useRef(null);
  const questionCompleteRef = useRef(null);
  const gameOverRef         = useRef(null);
  const adminRevealRef      = useRef(null);
  const bgRef               = useRef(null);
  // Track whether music was paused by an admin reveal (to resume after)
  const revealPausedBg      = useRef(false);

  useEffect(() => {
    const ext = (new Audio()).canPlayType('audio/ogg; codecs=vorbis') ? 'ogg' : 'mp3';
    const load = (name, opts = {}) => {
      const el = new Audio(`/sounds/${name}.${ext}`);
      el.preload = 'auto';
      Object.assign(el, opts);
      return el;
    };

    correctRef.current          = load('correct');
    incorrectRef.current        = load('incorrect');
    questionCompleteRef.current = load('question_complete');
    gameOverRef.current         = load('game_over');
    // Admin reveal uses question_complete as its SFX; swap to admin_reveal if
    // that file is later added to /sounds/. Falls back silently if absent.
    adminRevealRef.current      = load('admin_reveal');
    bgRef.current               = load('bg_music', { loop: true, volume: 0.38 });

    return () => {
      [correctRef, incorrectRef, questionCompleteRef, gameOverRef, adminRevealRef, bgRef].forEach(r => {
        if (!r.current) return;
        r.current.pause(); r.current.src = ''; r.current = null;
      });
    };
  }, []);

  const play = (ref, vol = 0.80) => {
    const el = ref.current;
    if (!el) return;
    el.currentTime = 0; el.volume = vol;
    el.play().catch(() => {});
  };

  // Unlock audio context on first user interaction (browser autoplay policy)
  const unlockedRef = useRef(false);
  const doUnlock = () => {
    if (unlockedRef.current) return;
    unlockedRef.current = true;
    [correctRef, incorrectRef, questionCompleteRef, gameOverRef, adminRevealRef, bgRef].forEach(r => {
      if (!r.current) return;
      const vol = r.current.volume;
      r.current.volume = 0;
      r.current.play().then(() => { r.current.pause(); r.current.currentTime = 0; r.current.volume = vol; }).catch(() => {});
    });
  };
  useEffect(() => {
    document.addEventListener('click', doUnlock, { once: true });
    document.addEventListener('keydown', doUnlock, { once: true });
    document.addEventListener('touchstart', doUnlock, { once: true });
    return () => {
      document.removeEventListener('click', doUnlock);
      document.removeEventListener('keydown', doUnlock);
      document.removeEventListener('touchstart', doUnlock);
    };
  }, []); // eslint-disable-line

  // Duck background music briefly while a sound effect plays
  const duckMusic = (durationMs = 1800, targetRatio = 0.20) => {
    const el = bgRef.current;
    if (!el || el.paused || el.volume < 0.01) return;
    const orig     = el.volume;
    const low      = orig * targetRatio;
    const downStep = (orig - low) / (200 / 50);
    const downIv   = setInterval(() => {
      if (!bgRef.current) { clearInterval(downIv); return; }
      bgRef.current.volume = Math.max(low, bgRef.current.volume - downStep);
      if (bgRef.current.volume <= low) clearInterval(downIv);
    }, 50);
    setTimeout(() => {
      if (!bgRef.current || bgRef.current.paused) return;
      const upStep = (orig - low) / (400 / 50);
      const upIv   = setInterval(() => {
        if (!bgRef.current) { clearInterval(upIv); return; }
        bgRef.current.volume = Math.min(orig, bgRef.current.volume + upStep);
        if (bgRef.current.volume >= orig) clearInterval(upIv);
      }, 50);
    }, durationMs);
  };

  return {
    correct()          { duckMusic(1600, 0.20); play(correctRef, 0.90); },
    incorrect()        { play(incorrectRef, 0.82); },
    questionComplete() { play(questionCompleteRef, 0.85); },

    // Admin reveal: stop BG music, play reveal SFX, mark that we paused BG
    // so resumeMusic() can restart it when the next question/continue fires.
    adminReveal() {
      const bg = bgRef.current;
      if (bg && !bg.paused) {
        // Fade out quickly then pause
        revealPausedBg.current = true;
        const step = bg.volume / (400 / 50);
        const iv = setInterval(() => {
          if (!bgRef.current) { clearInterval(iv); return; }
          bgRef.current.volume = Math.max(0, bgRef.current.volume - step);
          if (bgRef.current.volume <= 0) { bgRef.current.pause(); clearInterval(iv); }
        }, 50);
      }
      // Play the admin reveal SFX (falls back gracefully if file is missing)
      const el = adminRevealRef.current;
      if (el) {
        el.currentTime = 0; el.volume = 0.88;
        el.play().catch(() => {});
      }
    },

    // Call this when advancing to the next question or when a "continue"
    // action fires — restarts BG music only if it was stopped by adminReveal.
    resumeMusic() {
      if (!revealPausedBg.current) return;
      revealPausedBg.current = false;
      const el = bgRef.current;
      if (!el) return;
      el.currentTime = el.currentTime || 0;
      el.volume = 0;
      el.play().catch(() => {});
      const target = 0.38; const step = target / (2000 / 50);
      const iv = setInterval(() => {
        if (!bgRef.current) { clearInterval(iv); return; }
        bgRef.current.volume = Math.min(target, bgRef.current.volume + step);
        if (bgRef.current.volume >= target) clearInterval(iv);
      }, 50);
    },

    gameOver() {
      // Stop bg music so finale plays cleanly
      revealPausedBg.current = false; // clear any pending resume
      const bg = bgRef.current;
      if (bg && !bg.paused) { bg.volume = 0; bg.pause(); }
      play(gameOverRef, 0.88);
    },

    startMusic() {
      const el = bgRef.current;
      if (!el || !el.paused) return;
      el.currentTime = 0; el.volume = 0;
      el.play().catch(() => {});
      const target = 0.38; const step = target / (3000 / 50);
      const iv = setInterval(() => {
        if (!bgRef.current) { clearInterval(iv); return; }
        bgRef.current.volume = Math.min(target, bgRef.current.volume + step);
        if (bgRef.current.volume >= target) clearInterval(iv);
      }, 50);
    },

    stopMusic() {
      const el = bgRef.current;
      if (!el || el.paused) return;
      const step = el.volume / (1000 / 50);
      const iv = setInterval(() => {
        if (!bgRef.current) { clearInterval(iv); return; }
        bgRef.current.volume = Math.max(0, bgRef.current.volume - step);
        if (bgRef.current.volume <= 0) { bgRef.current.pause(); clearInterval(iv); }
      }, 50);
    },

    get unlocked() { return unlockedRef.current; },
    forceUnlock()  { doUnlock(); },
  };
}

// ─── Ably hook ────────────────────────────────────────────────────────────────
// Single shared Ably client (avoids N connections for N subscriptions)
let _ablyClient = null;
function getAblyClient() {
  if (!_ablyClient) _ablyClient = new Ably.Realtime({ authUrl: ABLY_AUTH_URL, autoConnect: true });
  return _ablyClient;
}

function useAblyChannel(channelName, handlers) {
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; });
  useEffect(() => {
    if (!channelName) return;
    const client  = getAblyClient();
    if (!client) return;
    const channel = client.channels.get(channelName);
    // Subscribe per-event so Ably routes correctly
    const subs = Object.keys(handlersRef.current).map(event => {
      const cb = (msg) => handlersRef.current[event]?.(msg);
      channel.subscribe(event, cb);
      return { event, cb };
    });
    return () => { subs.forEach(({ event, cb }) => channel.unsubscribe(event, cb)); };
  }, [channelName]);
}

// ─── Fireworks ────────────────────────────────────────────────────────────────
function Fireworks({ active }) {
  const cvs = useRef(null), raf = useRef(null), pts = useRef([]);
  useEffect(() => {
    if (!active) { cancelAnimationFrame(raf.current); return; }
    const c = cvs.current; if (!c) return;
    const ctx = c.getContext('2d');
    c.width = window.innerWidth; c.height = window.innerHeight;
    const COLS = ['#FFB300','#FF6B00','#FF3CAC','#7B2FFF','#00D4FF','#00FF88','#FFEC00','#FF4B4B'];
    const launch = () => {
      const x = 100 + Math.random() * (c.width - 200);
      const y = 50 + Math.random() * (c.height * .45);
      const col = COLS[Math.floor(Math.random() * COLS.length)];
      for (let i = 0; i < 110; i++) {
        const a = (Math.PI*2*i/110) + Math.random()*.4, s = 2.5 + Math.random()*9;
        pts.current.push({ x, y, vx:Math.cos(a)*s, vy:Math.sin(a)*s, col, alpha:1,
          r:1.8+Math.random()*3, grav:.065, drag:.983 });
      }
    };
    let n=0; const iv = setInterval(() => { launch(); if(++n>=12) clearInterval(iv); }, 280); launch();
    const loop = () => {
      ctx.fillStyle='rgba(10,8,20,.16)'; ctx.fillRect(0,0,c.width,c.height);
      pts.current = pts.current.filter(p=>p.alpha>.04);
      pts.current.forEach(p => {
        ctx.shadowBlur=12; ctx.shadowColor=p.col;
        ctx.beginPath(); ctx.arc(p.x,p.y,p.r,0,Math.PI*2);
        ctx.fillStyle=p.col+Math.floor(p.alpha*255).toString(16).padStart(2,'0');
        ctx.fill(); ctx.shadowBlur=0;
        ctx.beginPath(); ctx.arc(p.x,p.y,p.r*.35,0,Math.PI*2);
        ctx.fillStyle='#fff'+Math.floor(p.alpha*180).toString(16).padStart(2,'0'); ctx.fill();
        p.x+=p.vx; p.y+=p.vy; p.vx*=p.drag; p.vy*=p.drag; p.vy+=p.grav; p.alpha-=.012;
      });
      raf.current = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf.current); clearInterval(iv); };
  }, [active]);
  if (!active) return null;
  return <canvas ref={cvs} style={{ position:'fixed',inset:0,pointerEvents:'none',zIndex:200 }} />;
}

// ─── Timer Bar ────────────────────────────────────────────────────────────────
// TimerBar: single rAF loop drives both bar% and secsLeft — always in sync.
// cancelled=true freezes display immediately (admin reveal).
function TimerBar({ timerSeconds, startedAt, onExpired, cancelled = false }) {
  const [pct,      setPct]      = useState(100);
  const [secsLeft, setSecsLeft] = useState(timerSeconds || 0);
  const [expired,  setExpired]  = useState(false);
  const rafRef       = useRef(null);
  const firedRef     = useRef(false);
  const onExpiredRef = useRef(onExpired);
  useEffect(() => { onExpiredRef.current = onExpired; });

  useEffect(() => {
    if (cancelled) {
      cancelAnimationFrame(rafRef.current);
      return;
    }
    if (!timerSeconds || !startedAt) return;
    firedRef.current = false;
    setExpired(false);
    setPct(100);
    setSecsLeft(timerSeconds);

    const tick = () => {
      const elapsed   = (Date.now() - startedAt) / 1000;
      const remaining = Math.max(0, timerSeconds - elapsed);
      const p         = (remaining / timerSeconds) * 100;
      setPct(p);
      setSecsLeft(Math.ceil(remaining));
      if (remaining <= 0) {
        setExpired(true);
        if (!firedRef.current) { firedRef.current = true; onExpiredRef.current?.(); }
        return; // stop rAF
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [timerSeconds, startedAt, cancelled]);

  const barColor  = pct > 55 ? '#00D084' : pct > 25 ? '#FFB300' : '#FF4B4B';
  const glowColor = pct > 55 ? 'rgba(0,208,132,.6)' : pct > 25 ? 'rgba(255,179,0,.6)' : 'rgba(255,75,75,.7)';

  if (cancelled) return (
    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
      <div style={{ flex:1, height:10, borderRadius:99, background:'rgba(255,255,255,.06)' }} />
      <div style={{ minWidth:52, textAlign:'center', fontSize:12, fontWeight:700,
        fontFamily:"'Barlow Condensed',sans-serif", color:'rgba(255,255,255,.3)', letterSpacing:1 }}>
        —
      </div>
    </div>
  );

  return (
    <div style={{ display:'flex', alignItems:'center', gap:14 }}>
      <div style={{ flex:1, height:10, borderRadius:99, background:'rgba(255,255,255,.08)', overflow:'hidden' }}>
        <div style={{
          height:'100%', borderRadius:99,
          width:`${pct}%`,
          background:`linear-gradient(90deg, ${barColor}cc, ${barColor})`,
          boxShadow:`0 0 12px ${glowColor}, 0 0 20px ${glowColor}`,
          transition:'background .4s ease',
          // No CSS transition on width — rAF handles it smoothly
        }} />
      </div>
      <div style={{
        minWidth:60, textAlign:'center', fontSize:14, fontWeight:700,
        fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1,
        color: expired ? '#FF4B4B' : secsLeft <= 10 ? '#FF4B4B' : '#fff',
        animation: expired || secsLeft <= 5 ? 'timerPulse .5s ease infinite' : 'none',
      }}>
        {expired ? "TIME'S UP" : `${secsLeft}s`}
      </div>
    </div>
  );
}

// ─── Animated Score ───────────────────────────────────────────────────────────
function AnimatedScore({ value }) {
  const [disp, setDisp] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    if (prev.current === value) return;
    const from = prev.current; prev.current = value; let i = 0;
    const iv = setInterval(() => {
      i++; setDisp(Math.round(from + (value - from) * i / 25));
      if (i >= 25) clearInterval(iv);
    }, 28);
    return () => clearInterval(iv);
  }, [value]);
  return <>{disp}</>;
}

// ─── Answer Grid ─────────────────────────────────────────────────────────────
// Replaces ScatteredAnswers. Clean collision-free CSS grid that adapts to
// any answer count. Each slot has a fixed bounding box so text never overlaps.
function AnswerGrid({ answers, revealed, animKeys }) {
  const count = answers.length;
  // Column count: 1 col ≤2, 2 cols ≤8, 3 cols otherwise
  const cols  = count <= 2 ? 1 : count <= 8 ? 2 : 3;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: 'clamp(5px, .8vh, 10px)',
      width: '100%', height: '100%',
      padding: 'clamp(8px, 1.2vh, 16px)',
      paddingBottom: 'clamp(40px, 5vh, 52px)', // leave room for progress row
      alignContent: 'center',
    }}>
      {answers.map((ans, idx) => {
        const revEntry    = revealed[idx];
        const isRevealed  = !!revEntry;
        const isAnimating = !!animKeys[idx];
        const byAdmin     = revEntry?.revealedByAdmin;
        // Public surveys carry hint-only answers (no text) — revealed text comes
        // from the reveal entry written by the server on each correct answer.
        const answerText  = revEntry?.answerText || ans.text || '';
        const textLen     = (answerText || ans.hint || '').length || ans.len || 0;

        // Font size: start large, shrink as text gets longer, cap by column count.
        // Ceilings raised for big screens (55"+ TV / 4K) — the vw+vh preferred term
        // scales up, the px cap just stops it freezing small on large displays.
        const maxPx   = cols === 1 ? 88 : cols === 2 ? 64 : 46;
        const scaledPx = Math.max(14, maxPx - Math.max(0, textLen - 5) * (cols === 3 ? 1.4 : 1.7));
        const fsClamp  = `clamp(13px, ${(scaledPx / 16).toFixed(2)}vw + 0.7vh, ${scaledPx}px)`;

        // Hint text: server-computed for public surveys; fall back to deriving
        // it locally when full text is present (admin/legacy payloads)
        const hintText = ans.hint || (ans.text || '').toUpperCase().split(' ').map(w => {
          const vis = Math.min(2, w.length);
          return w.slice(0, vis) + '_ '.repeat(w.length - vis).trimEnd();
        }).join('  ');

        const revealedColor = byAdmin ? '#FFB300' : '#00E676';
        const revealedBg    = byAdmin
          ? 'linear-gradient(135deg,rgba(255,140,0,.22),rgba(255,179,0,.08))'
          : 'linear-gradient(135deg,rgba(0,200,83,.22),rgba(0,230,118,.08))';
        const revealedBorder = byAdmin
          ? 'rgba(255,179,0,.55)' : 'rgba(0,230,118,.55)';
        const revealedGlow   = byAdmin
          ? '0 0 22px rgba(255,140,0,.3),inset 0 1px 0 rgba(255,200,100,.18)'
          : '0 0 22px rgba(0,200,83,.22),inset 0 1px 0 rgba(0,230,118,.12)';

        return (
          <div
            key={idx}
            style={{
              display: 'flex', alignItems: 'center', gap: 'clamp(6px,1vw,12px)',
              padding: 'clamp(6px,.9vh,12px) clamp(8px,1.2vw,16px)',
              borderRadius: 'clamp(10px,1.2vw,16px)',
              background: isRevealed ? revealedBg : 'rgba(0,0,0,.38)',
              border: `1px solid ${isRevealed ? revealedBorder : 'rgba(255,255,255,.1)'}`,
              backdropFilter: 'blur(20px)',
              boxShadow: isRevealed ? revealedGlow : '0 2px 12px rgba(0,0,0,.4)',
              transition: 'background .35s ease, border-color .35s ease, box-shadow .35s ease, transform .3s cubic-bezier(.34,1.56,.64,1)',
              transform: isAnimating ? 'scale(1.04)' : 'scale(1)',
              animation: isAnimating ? 'wordReveal .55s cubic-bezier(.34,1.56,.64,1)' : 'none',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            {/* Rank badge */}
            <div style={{
              flexShrink: 0,
              width:  'clamp(24px,3vw,56px)',
              height: 'clamp(24px,3vw,56px)',
              borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: isRevealed ? `${revealedColor}28` : 'rgba(255,255,255,.07)',
              border: `1px solid ${isRevealed ? revealedColor + '60' : 'rgba(255,255,255,.14)'}`,
              fontSize: 'clamp(11px,1.5vw,28px)', fontWeight: 900,
              fontFamily: "'Barlow Condensed',sans-serif",
              color: isRevealed ? revealedColor : 'rgba(255,255,255,.3)',
              transition: 'all .35s ease',
            }}>{idx + 1}</div>

            {/* Text block */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: fsClamp, fontWeight: 900,
                fontFamily: "'Bebas Neue','Impact',sans-serif",
                letterSpacing: '0.06em',
                color: isRevealed ? '#fff' : 'rgba(255,255,255,.55)',
                textShadow: isRevealed
                  ? `0 0 18px ${revealedColor}cc, 0 2px 0 rgba(0,0,0,.8)`
                  : '0 2px 0 rgba(0,0,0,.6)',
                lineHeight: 1.1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                transition: 'color .35s ease, text-shadow .35s ease',
              }}>
                {isRevealed ? answerText.toUpperCase() : hintText}
              </div>
              {isRevealed && (revEntry?.playerName || byAdmin) && (
                <div style={{
                  fontSize: 'clamp(9px,.9vw,12px)', color: `${revealedColor}aa`,
                  fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: 2,
                  fontWeight: 700, marginTop: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  animation: 'rankBadge .4s cubic-bezier(.34,1.56,.64,1)',
                }}>
                  {byAdmin ? '👁 REVEALED' : `✓ ${revEntry.playerName}`}
                </div>
              )}
            </div>

            {/* Points */}
            <div style={{
              flexShrink: 0,
              fontSize: 'clamp(14px,2vw,40px)', fontWeight: 900,
              fontFamily: "'Bebas Neue',sans-serif", letterSpacing: 1,
              color: isRevealed ? revealedColor : 'rgba(255,255,255,.22)',
              textShadow: isRevealed ? `0 0 14px ${revealedColor}99` : 'none',
              transition: 'color .35s ease',
            }}>{ans.points}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Leaderboard Panel ────────────────────────────────────────────────────────
function LeaderboardPanel({ scores, teamScores, popWinner }) {
  const top    = [...(scores||[])].sort((a,b)=>b.score-a.score).slice(0,8);
  const metals = ['#FFD700','#C8C8C8','#CD7F32'];
  const hasTeams = teamScores?.length > 0;

  return (
    <div style={{
      background:'rgba(10,8,24,.94)',
      backdropFilter:'blur(32px)',
      border:'1px solid rgba(255,179,0,.25)',
      borderRadius:20,
      padding:'clamp(20px,2vh,34px) clamp(24px,1.9vw,40px)',
      boxShadow:'0 0 0 1px rgba(255,179,0,.07), 0 30px 80px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,179,0,.15)',
      minWidth:'clamp(290px,23vw,460px)',
    }}>
      {hasTeams && (
        <div style={{ marginBottom:14, paddingBottom:12, borderBottom:'1px solid rgba(255,179,0,.15)' }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:4, color:'rgba(255,179,0,.5)',
            fontFamily:"'Barlow Condensed',sans-serif", marginBottom:8, textTransform:'uppercase' }}>
            🏆 Teams
          </div>
          {teamScores.map((t,i) => (
            <div key={t.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 0',
              borderBottom: i<teamScores.length-1?'1px solid rgba(255,255,255,.04)':'none' }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:t.color, flexShrink:0,
                boxShadow:`0 0 8px ${t.color}` }} />
              <div style={{ flex:1, fontSize:'clamp(13px,1.1vw,22px)', fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif",
                color:i===0?'#FFD700':i===1?'#C8C8C8':i===2?'#CD7F32':'rgba(255,255,255,.6)',
                whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.name}</div>
              <div style={{ fontSize:'clamp(16px,1.4vw,28px)', fontWeight:900, fontFamily:"'Bebas Neue',sans-serif",
                color:i===0?'#FFD700':(t.color||'#FFB300'),
                textShadow:i===0?'0 0 14px rgba(255,215,0,.7)':'none' }}>{t.score}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{
        fontSize:'clamp(11px,.95vw,18px)', fontWeight:700, letterSpacing:5,
        color:'#FFB300', fontFamily:"'Barlow Condensed',sans-serif",
        marginBottom:16, textShadow:'0 0 20px rgba(255,179,0,.8)',
        textTransform:'uppercase', display:'flex', alignItems:'center', gap:8,
      }}>
        <span style={{ fontSize:'clamp(16px,1.3vw,26px)' }}>🏆</span> Rankings
      </div>

      {top.length === 0 && (
        <div style={{ color:'rgba(255,255,255,.15)', fontSize:12, textAlign:'center',
          padding:'16px 0', letterSpacing:3, fontFamily:"'Barlow Condensed',sans-serif" }}>
          AWAITING PLAYERS
        </div>
      )}

      {top.map((s, i) => {
        const isPopping = popWinner && i === 0;
        return (
          <div key={s.jid||i} style={{
            display:'flex', alignItems:'center', gap:10,
            padding:'10px 0',
            borderBottom: i < top.length-1 ? '1px solid rgba(255,255,255,.05)' : 'none',
            opacity: s.left ? .4 : 1,
          }}>
            <div style={{
              width:'clamp(30px,2.3vw,46px)', height:'clamp(30px,2.3vw,46px)', borderRadius:8, flexShrink:0,
              display:'flex', alignItems:'center', justifyContent:'center',
              background: i < 3 ? `${metals[i]}22` : 'rgba(255,255,255,.04)',
              border:`1px solid ${i<3 ? metals[i]+'55' : 'rgba(255,255,255,.08)'}`,
              fontSize:'clamp(13px,1.1vw,22px)', fontWeight:900,
              color: i < 3 ? metals[i] : 'rgba(255,255,255,.2)',
              fontFamily:"'Barlow Condensed',sans-serif",
              boxShadow: i===0 ? `0 0 16px ${metals[0]}55` : 'none',
            }}>{i+1}</div>

            <div style={{
              width:4, height:'clamp(30px,2.3vw,46px)', borderRadius:3, flexShrink:0,
              background: s.teamColor || (i<3 ? metals[i] : '#2a3a5a'),
              boxShadow:`0 0 8px ${s.teamColor||metals[i]||'#2a3a5a'}88`,
            }} />

            <div style={{ flex:1, minWidth:0 }}>
              <div style={{
                fontSize: i===0 ? 'clamp(18px,1.6vw,32px)' : 'clamp(14px,1.2vw,24px)', fontWeight:700,
                fontFamily:"'Barlow Condensed',sans-serif",
                color: i===0 ? '#FFD700' : i===1 ? '#C8C8C8' : i===2 ? '#CD7F32' : 'rgba(255,255,255,.75)',
                textShadow: i===0 ? '0 0 20px rgba(255,215,0,.7)' : 'none',
                whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                // Winner pop animation
                animation: isPopping ? 'winnerPop 1.2s cubic-bezier(.34,1.56,.64,1) both' : 'none',
                display:'inline-block',
              }}>{s.name}</div>
            </div>

            <div style={{
              fontSize: i===0 ? 'clamp(26px,2.2vw,46px)' : 'clamp(18px,1.5vw,32px)', fontWeight:900,
              fontFamily:"'Bebas Neue',sans-serif",
              color: i===0 ? '#FFD700' : (s.teamColor||'#FFB300'),
              textShadow: i===0 ? '0 0 20px rgba(255,215,0,.8)' : `0 0 10px ${s.teamColor||'#FFB300'}66`,
              letterSpacing:1,
              animation: isPopping ? 'winnerGlow 1.5s ease' : 'none',
            }}><AnimatedScore value={s.score} /></div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Nav Bar ──────────────────────────────────────────────────────────────────
function NavBar({ gameId, view, onViewChange, surveyName, status }) {
  const [copied, setCopied] = useState(false);
  const views = [
    { id:'display', label:'DISPLAY' },
    { id:'scores',  label:'SCORES'  },
    { id:'join',    label:'JOIN'    },
  ];
  const makeUrl = v => { const u = new URL(window.location.href); u.searchParams.set('view',v); return u.toString(); };
  const copyLink = () => { navigator.clipboard.writeText(makeUrl(view)); setCopied(true); setTimeout(()=>setCopied(false),2000); };

  return (
    <div style={{
      position:'relative', zIndex:100, height:'clamp(48px,7.5vh,70px)',
      padding:'0 clamp(12px,1.5vw,24px)', display:'flex', alignItems:'center', gap:'clamp(8px,1vw,14px)',
      background:'linear-gradient(180deg, rgba(8,6,20,.98) 0%, rgba(10,8,24,.96) 100%)',
      borderBottom:'1px solid rgba(255,179,0,.15)',
      boxShadow:'0 4px 40px rgba(0,0,0,.8), 0 1px 0 rgba(255,179,0,.08)',
      flexShrink:0,
    }}>
      <div style={{
        width:'clamp(30px,3.8vw,44px)', height:'clamp(30px,3.8vw,44px)', borderRadius:'clamp(8px,1vw,12px)', flexShrink:0,
        background:'linear-gradient(135deg, #FF8C00, #FFB300)',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:'clamp(16px,2vw,22px)', boxShadow:'0 0 20px rgba(255,140,0,.5)',
        animation:'pulseGlow 3s ease infinite',
      }}>🎯</div>

      <div style={{ flex:1, minWidth:0 }}>
        <div style={{
          fontSize:'clamp(13px,1.6vw,19px)', fontWeight:700,
          fontFamily:"'Barlow Condensed',sans-serif",
          color:'#fff', letterSpacing:3, textTransform:'uppercase',
          textShadow:'0 0 24px rgba(255,179,0,.5)',
          whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
        }}>America Says</div>
        <div style={{ display:'flex', gap:8, marginTop:1 }}>
          <span style={{ fontSize:'clamp(9px,1vw,13px)', color:'rgba(255,179,0,.4)', fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:3, fontWeight:600 }}>
            GAME {gameId}
          </span>
          {surveyName && <>
            <span style={{ color:'rgba(255,255,255,.1)', fontSize:'clamp(9px,1vw,13px)' }}>◆</span>
            <span style={{ fontSize:'clamp(9px,1vw,13px)', color:'rgba(255,255,255,.25)', fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, textTransform:'uppercase' }}>
              {surveyName}
            </span>
          </>}
        </div>
      </div>

      <div style={{ display:'flex', gap:2 }}>
        {views.map(v => (
          <button
            key={v.id}
            onClick={() => onViewChange(v.id)}
            style={{
              padding:'clamp(8px,1.1vh,13px) clamp(10px,1.2vw,16px)', borderRadius:7,
              fontSize:'clamp(9px,1vw,13px)', fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif",
              letterSpacing:2, textDecoration:'none', cursor:'pointer',
              color:      view===v.id ? '#FFB300'              : 'rgba(255,255,255,.22)',
              background: view===v.id ? 'rgba(255,179,0,.14)'  : 'transparent',
              border:     `1px solid ${view===v.id ? 'rgba(255,179,0,.4)' : 'transparent'}`,
              boxShadow:  view===v.id ? '0 0 12px rgba(255,179,0,.2)' : 'none',
              transition:'all .2s',
            }}>{v.label}</button>
        ))}
      </div>

      <button onClick={copyLink} style={{
        background: copied ? 'rgba(0,200,83,.15)' : 'rgba(255,179,0,.08)',
        border:`1px solid ${copied ? 'rgba(0,200,83,.4)' : 'rgba(255,179,0,.2)'}`,
        color: copied ? '#00C853' : 'rgba(255,179,0,.6)',
        padding:'clamp(8px,1.1vh,13px) clamp(10px,1.2vw,16px)', borderRadius:7, cursor:'pointer',
        fontSize:'clamp(9px,1vw,13px)', fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif",
        letterSpacing:2, transition:'all .2s',
      }}>{copied ? '✓ COPIED' : '⎘ LINK'}</button>

      {status === 'active' && (
        <div style={{
          display:'flex', alignItems:'center', gap:6,
          background:'rgba(255,75,75,.1)', border:'1px solid rgba(255,75,75,.35)',
          borderRadius:20, padding:'clamp(4px,.7vh,8px) clamp(9px,1.1vw,14px)',
          boxShadow:'0 0 16px rgba(255,75,75,.15)',
        }}>
          <div style={{
            width:'clamp(6px,.8vw,10px)', height:'clamp(6px,.8vw,10px)', borderRadius:'50%', background:'#FF4B4B',
            boxShadow:'0 0 10px #FF4B4B, 0 0 20px rgba(255,75,75,.6)',
            animation:'liveFlash .9s ease infinite',
          }} />
          <span style={{ fontSize:'clamp(9px,1vw,13px)', fontWeight:700, color:'#FF4B4B', fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:3 }}>LIVE</span>
        </div>
      )}
    </div>
  );
}

// ─── Scores Full View ─────────────────────────────────────────────────────────
function ScoresView({ scores }) {
  const top    = [...(scores||[])].sort((a,b)=>b.score-a.score);
  const metals = ['#FFD700','#C8C8C8','#CD7F32'];

  return (
    <div style={{ flex:1, padding:'32px 40px', overflow:'auto' }}>
      <div style={{
        fontSize:32, fontWeight:700, letterSpacing:6,
        color:'#FFD700', fontFamily:"'Bebas Neue',sans-serif",
        marginBottom:28, textTransform:'uppercase',
        textShadow:'0 0 28px rgba(255,215,0,.7)',
        display:'flex', alignItems:'center', gap:12,
      }}>
        <span>🏆</span> Leaderboard
      </div>

      {top.map((s, i) => (
        <div key={s.jid||i} style={{
          display:'flex', alignItems:'center', gap:18,
          padding:'16px 22px', marginBottom:10,
          background: i===0
            ? 'linear-gradient(135deg, rgba(255,215,0,.12), rgba(255,140,0,.04))'
            : 'rgba(255,255,255,.03)',
          border:`1px solid ${i<3 ? metals[i]+'44' : 'rgba(255,255,255,.06)'}`,
          borderRadius:16,
          boxShadow: i===0 ? '0 0 40px rgba(255,215,0,.1), inset 0 1px 0 rgba(255,215,0,.08)' : 'none',
          opacity: s.left ? .4 : 1,
        }}>
          <div style={{
            width:52, height:52, borderRadius:14, flexShrink:0,
            display:'flex', alignItems:'center', justifyContent:'center',
            background: i<3 ? `${metals[i]}22` : 'rgba(255,255,255,.04)',
            border:`1px solid ${i<3 ? metals[i]+'55' : 'rgba(255,255,255,.06)'}`,
            fontSize:22, fontWeight:900,
            color: i<3 ? metals[i] : 'rgba(255,255,255,.2)',
            fontFamily:"'Bebas Neue',sans-serif",
            boxShadow: i===0 ? `0 0 28px ${metals[0]}55` : 'none',
          }}>{i+1}</div>

          <div style={{
            width:5, height:52, borderRadius:3, flexShrink:0,
            background: s.teamColor || (i<3 ? metals[i] : '#2a3a5a'),
            boxShadow:`0 0 14px ${s.teamColor||metals[i]||'#2a3a5a'}99`,
          }} />

          <div style={{ flex:1, minWidth:0 }}>
            <div style={{
              fontSize: i===0 ? 34 : i===1 ? 26 : 22, fontWeight:700,
              fontFamily:"'Barlow Condensed',sans-serif",
              color: i===0 ? '#FFD700' : i===1 ? '#C8C8C8' : i===2 ? '#CD7F32' : 'rgba(255,255,255,.85)',
              textShadow: i===0 ? '0 0 28px rgba(255,215,0,.85)' : 'none',
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
            }}>{s.name}</div>
          </div>

          <div style={{
            fontSize: i===0 ? 52 : i<3 ? 38 : 28, fontWeight:900,
            fontFamily:"'Bebas Neue',sans-serif", letterSpacing:1,
            color: i===0 ? '#FFD700' : (s.teamColor||'#FFB300'),
            textShadow: i===0 ? '0 0 32px rgba(255,215,0,.9)' : `0 0 16px ${s.teamColor||'#FFB300'}77`,
          }}><AnimatedScore value={s.score} /></div>
        </div>
      ))}

      {top.length === 0 && (
        <div style={{ color:'rgba(255,255,255,.15)', textAlign:'center', padding:'60px 0',
          fontSize:24, fontFamily:"'Bebas Neue',sans-serif", letterSpacing:6 }}>
          NO SCORES YET
        </div>
      )}
    </div>
  );
}

// ─── Join View ────────────────────────────────────────────────────────────────
function JoinView({ gameId, surveyName, scores }) {
  const displayUrl = `${window.location.origin}${window.location.pathname}?game=${gameId}&type=americasays&view=display`;
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(displayUrl); setCopied(true); setTimeout(()=>setCopied(false),2000); };

  return (
    <div style={{
      flex:1, display:'flex', alignItems:'center', justifyContent:'center',
      gap:60, padding:'40px 60px', flexWrap:'wrap', overflow:'auto',
    }}>
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:18 }}>
        <div style={{ fontSize:11, letterSpacing:5, color:'rgba(255,179,0,.6)',
          fontFamily:"'Barlow Condensed',sans-serif", marginBottom:4, fontWeight:700 }}>
          DISPLAY SCREEN LINK
        </div>
        <div style={{
          padding:14, background:'rgba(255,179,0,.06)',
          border:'2px solid rgba(255,179,0,.3)', borderRadius:18,
          boxShadow:'0 0 50px rgba(255,140,0,.15)',
        }}>
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(displayUrl)}&color=FFB300&bgcolor=0a0818&format=png&qzone=1`}
            alt="QR" width={220} height={220} style={{ display:'block', borderRadius:8 }}
          />
        </div>
        <button onClick={copy} style={{
          background: copied ? 'rgba(0,200,83,.15)' : 'rgba(255,179,0,.1)',
          border:`1px solid ${copied ? 'rgba(0,200,83,.4)' : 'rgba(255,179,0,.3)'}`,
          color: copied ? '#00C853' : '#FFB300',
          padding:'9px 22px', borderRadius:9, cursor:'pointer',
          fontSize:11, fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2,
        }}>{copied ? '✓ COPIED!' : '⎘ COPY LINK'}</button>
      </div>

      <div style={{ width:1, height:400, background:'rgba(255,179,0,.1)', flexShrink:0 }} />

      <div style={{ maxWidth:400, display:'flex', flexDirection:'column', gap:28 }}>
        <div>
          <div style={{ fontSize:44, fontWeight:700, fontFamily:"'Bebas Neue',sans-serif",
            color:'#fff', letterSpacing:4, textShadow:'0 0 30px rgba(255,179,0,.5)' }}>
            HOW TO PLAY
          </div>
          <div style={{ fontSize:13, color:'rgba(255,179,0,.7)',
            fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:4, fontWeight:600 }}>
            VIA WHATSAPP
          </div>
        </div>
        {[
          { n:'1', t:'Send your answer to the WhatsApp number' },
          { n:'2', t:'Watch it appear live on the board!' },
          { n:'3', t:'Earn points for each correct answer' },
        ].map(({ n, t }) => (
          <div key={n} style={{ display:'flex', gap:14, alignItems:'flex-start' }}>
            <div style={{
              width:38, height:38, borderRadius:10, flexShrink:0,
              background:'rgba(255,179,0,.12)', border:'1px solid rgba(255,179,0,.3)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:20, fontWeight:900, color:'#FFB300',
              fontFamily:"'Bebas Neue',sans-serif", boxShadow:'0 0 16px rgba(255,179,0,.18)',
            }}>{n}</div>
            <div style={{ fontSize:18, color:'rgba(255,255,255,.75)',
              fontFamily:"'Barlow Condensed',sans-serif", lineHeight:1.5, fontWeight:600 }}>{t}</div>
          </div>
        ))}

        <div style={{
          padding:'18px 26px',
          background:'rgba(255,215,0,.08)', border:'1px solid rgba(255,215,0,.28)',
          borderRadius:16, display:'flex', alignItems:'center', gap:18,
          boxShadow:'0 0 30px rgba(255,215,0,.1)',
        }}>
          <div>
            <div style={{ fontSize:10, color:'rgba(255,215,0,.6)',
              fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:5, marginBottom:4, fontWeight:700 }}>GAME CODE</div>
            <div style={{ fontSize:40, fontWeight:900, color:'#FFD700',
              fontFamily:"'Bebas Neue',sans-serif", letterSpacing:8,
              textShadow:'0 0 30px rgba(255,215,0,.8)' }}>{gameId}</div>
          </div>
          <div style={{ flex:1 }} />
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:12, color:'rgba(255,215,0,.5)',
              fontFamily:"'Barlow Condensed',sans-serif", fontWeight:600 }}>{surveyName||'AMERICA SAYS'}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,.2)',
              fontFamily:"'Barlow Condensed',sans-serif", marginTop:4 }}>
              {scores.length} player{scores.length!==1?'s':''}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Waiting Screen ───────────────────────────────────────────────────────────
function WaitingScreen({ gameId, gameName, game }) {
  const isTeamGame = game?.mode === 'teams' && game?.teams?.length > 0;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      flex:'1 0 auto', gap:'clamp(10px,2.5vh,28px)', animation:'fadeIn .8s ease',
      padding:'clamp(12px,2.5vh,32px) clamp(16px,3vw,48px)' }}>
      <div style={{ fontSize:'clamp(44px,min(8vh,10vw),90px)', filter:'drop-shadow(0 0 50px rgba(255,140,0,.7))',
        animation:'pulseGlow 2.5s ease infinite' }}>🎯</div>
      <h1 style={{ fontSize:'clamp(28px,min(6vh,6vw),64px)', fontWeight:700, color:'#fff', textAlign:'center',
        fontFamily:"'Bebas Neue',sans-serif", textTransform:'uppercase', letterSpacing:4,
        textShadow:'0 0 40px rgba(255,179,0,.5)' }}>
        {gameName || 'America Says'}
      </h1>
      <p style={{ color:'rgba(255,255,255,.4)', fontSize:'clamp(13px,min(1.8vh,1.8vw),22px)',
        fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, fontWeight:600,
        textAlign:'center' }}>Get ready — game starts soon!</p>
      <div style={{
        background:'rgba(0,0,0,.4)', border:'2px solid rgba(255,179,0,.35)',
        borderRadius:20, padding:'clamp(12px,2vh,22px) clamp(24px,4vw,52px)', textAlign:'center',
        backdropFilter:'blur(16px)', boxShadow:'0 0 40px rgba(255,140,0,.12)',
      }}>
        <div style={{ fontSize:'clamp(9px,1.1vw,13px)', color:'rgba(255,255,255,.3)', fontWeight:700,
          textTransform:'uppercase', letterSpacing:4, marginBottom:6,
          fontFamily:"'Barlow Condensed',sans-serif" }}>Game Code</div>
        <div style={{ fontSize:'clamp(32px,min(5.5vh,6vw),60px)', fontWeight:900, color:'#FFB300',
          letterSpacing:10, fontFamily:"'Bebas Neue',sans-serif",
          textShadow:'0 0 24px rgba(255,179,0,.6)' }}>{gameId}</div>
      </div>

      {isTeamGame && (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'clamp(8px,1.2vh,14px)',
          width:'100%', maxWidth:'clamp(320px,60vw,640px)' }}>
          <div style={{ fontSize:'clamp(10px,min(1.4vh,1.3vw),15px)', color:'rgba(255,179,0,.55)', fontWeight:700,
            letterSpacing:4, fontFamily:"'Barlow Condensed',sans-serif", textTransform:'uppercase' }}>
            Team Game — Text /join {gameId} [team] to choose your team
          </div>
          <div style={{ display:'flex', gap:'clamp(8px,1.5vw,18px)', flexWrap:'wrap', justifyContent:'center' }}>
            {game.teams.map((t, i) => (
              <div key={t.id} style={{
                display:'flex', alignItems:'center', gap:'clamp(5px,.8vw,8px)',
                background:`${t.color}1A`, border:`2px solid ${t.color}66`,
                borderRadius:12, padding:'clamp(8px,1.2vh,14px) clamp(14px,2.2vw,24px)',
                fontFamily:"'Bebas Neue',sans-serif",
              }}>
                <div style={{ width:'clamp(10px,1.3vw,16px)', height:'clamp(10px,1.3vw,16px)',
                  borderRadius:'50%', background:t.color, flexShrink:0,
                  boxShadow:`0 0 12px ${t.color}88` }} />
                <span style={{ fontSize:'clamp(14px,min(2vh,2.5vw),28px)', color:t.color, letterSpacing:3 }}>
                  {i + 1}. {t.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Animated Background ──────────────────────────────────────────────────────
// Replaces the plain dark bg with a living, depth-rich animated field
function AnimatedBackground() {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none', overflow:'hidden' }}>
      {/* Deep space base */}
      <div style={{
        position:'absolute', inset:0,
        background:'radial-gradient(ellipse at 50% 30%, #12083a 0%, #0a0818 55%, #020108 100%)',
      }} />

      {/* Aurora bands */}
      <div style={{
        position:'absolute', top:'15%', left:'-20%', right:'-20%', height:'35%',
        background:'linear-gradient(180deg, transparent 0%, rgba(60,20,140,.18) 40%, rgba(30,100,180,.12) 60%, transparent 100%)',
        transform:'rotate(-8deg)',
        animation:'auroraPulse 12s ease infinite',
        filter:'blur(40px)',
      }} />
      <div style={{
        position:'absolute', bottom:'10%', left:'-20%', right:'-20%', height:'30%',
        background:'linear-gradient(180deg, transparent 0%, rgba(0,140,200,.1) 50%, transparent 100%)',
        transform:'rotate(6deg)',
        animation:'auroraPulse 16s ease infinite 4s',
        filter:'blur(50px)',
      }} />

      {/* Large slow orbs */}
      <div style={{
        position:'absolute', width:'80vw', height:'80vw',
        top:'-30%', left:'-25%',
        background:'radial-gradient(circle, rgba(255,130,0,.07) 0%, transparent 60%)',
        animation:'bgOrb1 18s ease infinite',
      }} />
      <div style={{
        position:'absolute', width:'70vw', height:'70vw',
        bottom:'-25%', right:'-20%',
        background:'radial-gradient(circle, rgba(100,50,220,.07) 0%, transparent 60%)',
        animation:'bgOrb2 22s ease infinite 3s',
      }} />
      <div style={{
        position:'absolute', width:'50vw', height:'50vw',
        top:'30%', left:'25%',
        background:'radial-gradient(circle, rgba(0,160,220,.05) 0%, transparent 60%)',
        animation:'bgOrb3 28s ease infinite 8s',
      }} />
      <div style={{
        position:'absolute', width:'35vw', height:'35vw',
        top:'55%', right:'10%',
        background:'radial-gradient(circle, rgba(255,60,180,.04) 0%, transparent 60%)',
        animation:'bgOrb4 20s ease infinite 5s',
      }} />

      {/* Star field */}
      {Array.from({length:60}, (_,i) => {
        const x = (i * 137.508) % 100;
        const y = (i * 97.31 + 13) % 100;
        const size = i % 5 === 0 ? 2.5 : i % 3 === 0 ? 1.8 : 1.2;
        const delay = (i * 0.4) % 6;
        const dur   = 2 + (i * 0.37) % 4;
        return (
          <div key={i} style={{
            position:'absolute',
            left:`${x}%`, top:`${y}%`,
            width:size, height:size,
            borderRadius:'50%',
            background:'#fff',
            opacity:.3,
            animation:`starTwinkle ${dur}s ease ${delay}s infinite`,
          }} />
        );
      })}

      {/* Subtle grid */}
      <div style={{
        position:'absolute', inset:0,
        backgroundImage:`linear-gradient(rgba(255,255,255,.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.018) 1px,transparent 1px)`,
        backgroundSize:'80px 80px',
      }} />

      {/* Horizontal light streams (occasional) */}
      <div style={{
        position:'absolute', top:'28%', left:0, right:0, height:1,
        background:'linear-gradient(90deg,transparent,rgba(255,179,0,.12),rgba(0,200,220,.08),transparent)',
        animation:'streamLine 14s linear infinite',
        filter:'blur(1px)',
      }} />
      <div style={{
        position:'absolute', top:'68%', left:0, right:0, height:1,
        background:'linear-gradient(90deg,transparent,rgba(180,60,220,.08),rgba(0,180,255,.06),transparent)',
        animation:'streamLine 19s linear infinite 6s',
        filter:'blur(1px)',
      }} />
    </div>
  );
}

// ─── Studio Background (Classic TV Show view) ────────────────────────────────
// TV-studio ambiance: deep navy blue, spotlight cones from above, slow sweep
// beams — deliberately NOT the space/orb feel of the main animated background.
function StudioBackground() {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none', overflow:'hidden' }}>
      {/* Deep navy studio base */}
      <div style={{ position:'absolute', inset:0,
        background:'linear-gradient(180deg, #04123a 0%, #020c28 45%, #010510 100%)' }} />

      {/* Upper-center spotlight — main TV backdrop */}
      <div style={{ position:'absolute', top:'-25%', left:'10%', right:'10%', height:'80%',
        background:'radial-gradient(ellipse at 50% 0%, rgba(20,70,210,.32) 0%, rgba(10,40,150,.1) 40%, transparent 70%)',
        animation:'studioGlow 9s ease infinite', pointerEvents:'none' }} />

      {/* Left spotlight cone */}
      <div style={{ position:'absolute', top:'-5%', left:'-15%', width:'55%', height:'100%',
        background:'radial-gradient(ellipse at 0% 0%, rgba(15,50,190,.18) 0%, transparent 60%)',
        transform:'rotate(10deg)', animation:'studioGlow 13s ease 2s infinite' }} />

      {/* Right spotlight cone */}
      <div style={{ position:'absolute', top:'-5%', right:'-15%', width:'55%', height:'100%',
        background:'radial-gradient(ellipse at 100% 0%, rgba(15,50,190,.18) 0%, transparent 60%)',
        transform:'rotate(-10deg)', animation:'studioGlow 13s ease 6s infinite' }} />

      {/* Warm amber floor wash — lower third */}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'30%',
        background:'radial-gradient(ellipse at 50% 100%, rgba(255,140,0,.07) 0%, transparent 70%)',
        animation:'studioGlow 11s ease 4s infinite' }} />

      {/* Subtle USA-star dot pattern (very faint) */}
      <div style={{ position:'absolute', inset:0,
        backgroundImage:'radial-gradient(circle, rgba(255,255,255,.03) 1.2px, transparent 1.2px)',
        backgroundSize:'38px 38px' }} />

      {/* Slow horizontal beam sweeps (TV scan line feel) */}
      <div style={{ position:'absolute', top:'22%', height:2, left:'-40%', width:'50%',
        background:'linear-gradient(90deg, transparent, rgba(100,160,255,.15), transparent)',
        filter:'blur(1.5px)', animation:'studioSweep 26s linear infinite' }} />
      <div style={{ position:'absolute', top:'65%', height:2, left:'-40%', width:'50%',
        background:'linear-gradient(90deg, transparent, rgba(100,160,255,.08), transparent)',
        filter:'blur(1.5px)', animation:'studioSweep 34s linear infinite 12s' }} />

      {/* Red/white/blue bottom stripe (very subtle USA flag nod) */}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:3,
        background:'linear-gradient(90deg, rgba(180,0,0,.35) 0%, rgba(180,0,0,.35) 33%, rgba(255,255,255,.08) 33%, rgba(255,255,255,.08) 36%, rgba(0,40,130,.4) 36%, rgba(0,40,130,.4) 100%)',
        pointerEvents:'none' }} />
    </div>
  );
}

// ─── Scatter Board ────────────────────────────────────────────────────────────
// Full-screen floating word display matching the reference screenshot:
// - Dark navy/midnight background (same AnimatedBackground as grid view)
// - Words at true scatter positions spread across the full viewport
// - Centre word enormous, outer words progressively smaller
// - Gold glow on all revealed words, dim white for unrevealed
// - Staggered pop-in entrance, gentle per-word bob
// - Compact question banner at top, status + toggle at bottom
function ScatterBoard({ answers, revealed, animKeys, question, currentQ, totalQuestions,
                        timerInfo, timerExpired, timerCancelled, onExpired }) {
  const count = answers.length;

  // ── Scatter position presets ─────────────────────────────────────────────────
  // Each entry: { left, top } as % of play-field, size = 'hero'|'large'|'mid'|'small'|'xs'
  // Positions are chosen to spread words across the FULL viewport with no overlap.
  // Derived directly from pixel-measuring the reference screenshots.
  const PRESETS = {
    1:  [{ left:50, top:50, size:'hero' }],
    2:  [
      { left:32, top:42, size:'large' },
      { left:68, top:58, size:'large' },
    ],
    3:  [
      { left:24, top:35, size:'mid'  },
      { left:50, top:54, size:'hero' },
      { left:76, top:35, size:'mid'  },
    ],
    4:  [
      { left:22, top:32, size:'mid'   },
      { left:76, top:32, size:'mid'   },
      { left:50, top:54, size:'hero'  },
      { left:50, top:79, size:'mid'   },
    ],
    5:  [
      { left:50, top:20, size:'small' },
      { left:20, top:40, size:'mid'   },
      { left:78, top:40, size:'mid'   },
      { left:50, top:58, size:'hero'  },
      { left:26, top:76, size:'mid'   },
    ],
    6:  [
      { left:50, top:18, size:'small' },
      { left:18, top:36, size:'mid'   },
      { left:78, top:36, size:'mid'   },
      { left:50, top:55, size:'hero'  },
      { left:20, top:73, size:'mid'   },
      { left:78, top:73, size:'mid'   },
    ],
    7:  [
      { left:50, top:16, size:'small' },
      { left:16, top:33, size:'mid'   },
      { left:78, top:33, size:'mid'   },
      { left:50, top:51, size:'hero'  },
      { left:16, top:68, size:'mid'   },
      { left:78, top:68, size:'mid'   },
      { left:50, top:84, size:'small' },
    ],
    // 8 = Jamaica Says reference layout (SHELL in centre)
    8:  [
      { left:38, top:41, size:'mid'   },
      { left:62, top:41, size:'mid'   },
      { left:14, top:52, size:'small' },
      { left:84, top:52, size:'mid'   },
      { left:50, top:56, size:'hero'  },
      { left:28, top:69, size:'small' },
      { left:70, top:69, size:'small' },
      { left:50, top:82, size:'mid'   },
    ],
    9:  [
      { left:50, top:15, size:'small' },
      { left:18, top:30, size:'small' },
      { left:78, top:30, size:'small' },
      { left:14, top:48, size:'mid'   },
      { left:50, top:53, size:'hero'  },
      { left:83, top:48, size:'mid'   },
      { left:20, top:67, size:'mid'   },
      { left:78, top:67, size:'mid'   },
      { left:50, top:83, size:'small' },
    ],
    // 10-12: use full-viewport grid scatter, no hero — equal small/mid sizes
    10: [
      { left:50, top:13, size:'small' },
      { left:20, top:25, size:'small' },
      { left:78, top:25, size:'small' },
      { left:10, top:42, size:'mid'   },
      { left:50, top:42, size:'mid'   },
      { left:85, top:42, size:'mid'   },
      { left:10, top:60, size:'mid'   },
      { left:50, top:60, size:'mid'   },
      { left:85, top:60, size:'mid'   },
      { left:50, top:78, size:'small' },
    ],
    11: [
      { left:50, top:11, size:'small' },
      { left:18, top:22, size:'small' },
      { left:78, top:22, size:'small' },
      { left: 8, top:38, size:'mid'   },
      { left:38, top:38, size:'mid'   },
      { left:68, top:38, size:'mid'   },
      { left:50, top:54, size:'mid'   },
      { left: 8, top:68, size:'mid'   },
      { left:38, top:68, size:'mid'   },
      { left:68, top:68, size:'mid'   },
      { left:50, top:83, size:'small' },
    ],
    12: [
      { left:50, top:10, size:'small' },
      { left:20, top:20, size:'small' },
      { left:78, top:20, size:'small' },
      { left: 6, top:35, size:'mid'   },
      { left:35, top:35, size:'mid'   },
      { left:65, top:35, size:'mid'   },
      { left:88, top:35, size:'small' },
      { left: 6, top:56, size:'mid'   },
      { left:35, top:56, size:'mid'   },
      { left:65, top:56, size:'mid'   },
      { left:88, top:56, size:'small' },
      { left:50, top:78, size:'small' },
    ],
  };

  // Font sizes. For large answer counts (10+) we suppress 'hero' entirely.
  // Sizes use vw so they scale properly on any TV screen size.
  const needsHero = count <= 9;
  const SIZE_FS = {
    hero:  needsHero ? 'clamp(52px,8vw,104px)' : 'clamp(28px,3.5vw,46px)',
    large: 'clamp(36px,5vw,68px)',
    mid:   count <= 9
      ? 'clamp(22px,3.2vw,44px)'
      : 'clamp(18px,2.4vw,32px)',
    small: count <= 9
      ? 'clamp(15px,2vw,28px)'
      : 'clamp(13px,1.6vw,22px)',
    xs:    'clamp(11px,1.3vw,17px)',
  };

  // Build slots: use preset if available, otherwise generate a grid
  const getSlots = () => {
    const preset = PRESETS[count];
    if (preset) return preset.map(p => ({ ...p, fs: SIZE_FS[p.size] }));

    // Dynamic fallback for >12 answers: fill a 4-column grid top-to-bottom
    return answers.map((_, idx) => {
      const cols = 4;
      const rows = Math.ceil(count / cols);
      const col  = idx % cols;
      const row  = Math.floor(idx / cols);
      const left = 8 + (col / (cols - 1)) * 84;
      const top  = 10 + (row / Math.max(rows - 1, 1)) * 80;
      return { left, top, fs: SIZE_FS.xs };
    });
  };

  const slots = getSlots();

  // ── Hint: first 2 chars of each word + underscores ───────────────────────────
  const makeHint = (text) =>
    (text || '').toUpperCase().split(' ').map(w => {
      const vis = Math.min(2, w.length);
      return w.slice(0, vis) + '_'.repeat(w.length - vis);
    }).join(' ');

  const BOB_ANIMS = ['scatterBobA','scatterBobB','scatterBobC'];

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', position:'relative' }}>

      {/* ── Question Banner ─────────────────────────────────────────────────── */}
      <div style={{
        display:'flex', alignItems:'stretch', flexShrink:0,
        animation:'scatterBannerIn .5s ease both',
        position:'relative', zIndex:10,
      }}>
        {/* Orange number badge */}
        <div style={{
          width:'clamp(46px,5vw,68px)',
          minHeight:'clamp(46px,6vh,66px)',
          borderRadius:'clamp(8px,1vw,14px) 0 0 clamp(8px,1vw,14px)',
          background:'linear-gradient(160deg,#FF9500,#FF5000)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontFamily:"'Bebas Neue',sans-serif",
          fontSize:'clamp(28px,4vw,48px)', fontWeight:900, color:'#fff',
          flexShrink:0,
          boxShadow:'inset 0 -3px 0 rgba(0,0,0,.28), 0 4px 20px rgba(255,100,0,.55)',
        }}>
          {(currentQ||0)+1}
        </div>
        {/* Purple-blue prompt */}
        <div style={{
          flex:1,
          minHeight:'clamp(46px,6vh,66px)',
          background:'linear-gradient(135deg,#2D1B72 0%,#3730A3 50%,#4338CA 100%)',
          borderRadius:`0 clamp(8px,1vw,14px) clamp(8px,1vw,14px) 0`,
          padding:'0 clamp(16px,2.5vw,28px) 0 clamp(14px,1.8vw,20px)',
          boxShadow:'0 4px 30px rgba(55,48,163,.7), inset 0 1px 0 rgba(255,255,255,.1)',
          position:'relative', overflow:'hidden', display:'flex', alignItems:'center',
        }}>
          {/* Gloss top shine */}
          <div style={{ position:'absolute', top:0, left:0, right:0, height:'42%',
            background:'rgba(255,255,255,.07)', pointerEvents:'none' }} />
          {/* Scan sweep */}
          <div style={{ position:'absolute', left:0, right:0, height:1,
            background:'linear-gradient(90deg,transparent,rgba(255,255,255,.22),transparent)',
            animation:'scanline 3s linear infinite', pointerEvents:'none' }} />
          <div style={{
            fontSize:'clamp(14px,2vw,26px)', fontWeight:700,
            fontFamily:"'Barlow Condensed',sans-serif",
            fontStyle:'italic', textTransform:'uppercase', letterSpacing:'0.08em',
            color:'#fff', textShadow:'0 2px 8px rgba(0,0,0,.6)',
            lineHeight:1.15, position:'relative', zIndex:1,
          }}>
            {question.prompt}
          </div>
          {totalQuestions > 1 && (
            <div style={{
              position:'absolute', right:14, top:'50%', transform:'translateY(-50%)',
              fontSize:'clamp(8px,.9vw,11px)', color:'rgba(255,255,255,.28)',
              fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, fontWeight:700,
            }}>
              {(currentQ||0)+1}/{totalQuestions}
            </div>
          )}
        </div>
      </div>

      {/* ── Scatter play field ───────────────────────────────────────────────── */}
      <div style={{ flex:1, position:'relative', minHeight:0, overflow:'hidden' }}>
        {answers.map((ans, idx) => {
          const revEntry    = revealed[idx];
          const isRevealed  = !!revEntry;
          const byAdmin     = revEntry?.revealedByAdmin;
          const isAnimating = !!animKeys[idx];
          const slot        = slots[idx];
          const displayText = isRevealed
            ? (revEntry?.answerText || ans.text || '').toUpperCase()
            : (ans.hint || makeHint(ans.text));

          // Entrance stagger: 100ms per word
          const staggerDelay = `${idx * 0.10}s`;

          // Continuous gentle bob — different period per word so they don't sync
          const bobDur  = `${5 + (idx * 1.3) % 3.5}s`;
          const bobAnim = `${BOB_ANIMS[idx % 3]} ${bobDur} ease-in-out ${staggerDelay} infinite`;

          // Unrevealed: dim blue-white matching the reference screenshot
          // Revealed: bright white with warm gold glow
          const color = isRevealed ? '#ffffff' : 'rgba(200,218,255,0.55)';
          const textShadow = isRevealed
            ? '0 0 20px rgba(255,205,0,.8), 0 0 50px rgba(255,150,0,.55), 0 0 90px rgba(255,80,0,.2), 0 2px 0 rgba(0,0,0,.9)'
            : '0 1px 8px rgba(0,0,40,.9)';

          return (
            <div
              key={idx}
              style={{
                position: 'absolute',
                left: `${slot.left}%`,
                top:  `${slot.top}%`,
                // Static centering — every keyframe that touches transform MUST
                // also include translate(-50%,-50%) to avoid a position jump.
                transform: 'translate(-50%,-50%)',
                textAlign: 'center',
                pointerEvents: 'none',
                zIndex: slot.size === 'hero' ? 6 : slot.size === 'large' ? 5 : slot.size === 'mid' ? 4 : 3,
                // Staggered pop-in entrance
                animation: `scatterWordIn .65s cubic-bezier(.34,1.45,.64,1) ${staggerDelay} both`,
              }}
            >
              {/* Gold halo disc behind revealed words */}
              {isRevealed && (
                <div style={{
                  position:'absolute', left:'50%', top:'45%',
                  transform:'translate(-50%,-50%)',
                  width:'300%', height:'260%',
                  borderRadius:'50%',
                  background:'radial-gradient(circle, rgba(255,170,0,.14) 0%, rgba(255,100,0,.06) 45%, transparent 70%)',
                  pointerEvents:'none', zIndex:0,
                  animation:'scatterGoldPulse 3.2s ease infinite',
                }} />
              )}

              {/* The word(s) */}
              <div style={{
                fontFamily: "'Bebas Neue','Impact',sans-serif",
                fontSize: slot.fs,
                fontWeight: 900,
                letterSpacing: '0.05em',
                lineHeight: 1,
                position:'relative', zIndex:1,
                color,
                textShadow,
                whiteSpace: 'nowrap',
                animation: isAnimating
                  ? 'scatterRevealBurst .75s ease both'
                  : isRevealed
                    ? `scatterGoldPulse 3.2s ease ${staggerDelay} infinite, ${bobAnim}`
                    : bobAnim,
              }}>
                {displayText}
              </div>

              {/* Attribution tag — tight below the word */}
              {isRevealed && (revEntry?.playerName || byAdmin) && (
                <div style={{
                  position:'relative', zIndex:2,
                  marginTop:'clamp(2px,.25vh,4px)',
                  fontSize:'clamp(7px,.65vw,9px)',
                  color:'rgba(255,195,50,.85)',
                  fontFamily:"'Barlow Condensed',sans-serif",
                  letterSpacing:'0.22em', fontWeight:700,
                  textTransform:'uppercase',
                  animation:'rankBadge .45s cubic-bezier(.34,1.56,.64,1) both',
                }}>
                  {byAdmin ? '👁 REVEALED' : `✓ ${revEntry.playerName}`}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Bottom status bar ────────────────────────────────────────────────── */}
      <div style={{
        flexShrink:0, position:'relative', zIndex:10,
        animation:'scatterStatusIn .5s ease .25s both',
      }}>
        {timerInfo.timerSeconds > 0 && (<>
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            marginBottom:5,
          }}>
            {/* Round badge — solid green pill bottom-left */}
            <div style={{
              background:'linear-gradient(135deg,#16a34a,#15803d)',
              borderRadius:7, padding:'3px 14px',
              boxShadow:'0 2px 10px rgba(22,163,74,.5)',
            }}>
              <span style={{
                fontSize:'clamp(10px,1.2vw,15px)', fontWeight:900,
                fontFamily:"'Bebas Neue',sans-serif",
                letterSpacing:'0.1em', color:'#fff',
              }}>
                Round {(currentQ||0)+1}
              </span>
            </div>

            {/* Right status */}
            {timerExpired && !timerCancelled && (
              <span style={{
                fontSize:'clamp(9px,.9vw,11px)', color:'#FF4B4B', fontWeight:700,
                textTransform:'uppercase', letterSpacing:2,
                fontFamily:"'Barlow Condensed',sans-serif",
                animation:'timerPulse .6s ease infinite',
              }}>✕ TIME'S UP</span>
            )}
            {timerCancelled && (
              <span style={{
                fontSize:'clamp(9px,.9vw,11px)', color:'rgba(255,195,50,.9)', fontWeight:700,
                textTransform:'uppercase', letterSpacing:2,
                fontFamily:"'Barlow Condensed',sans-serif",
              }}>👁 REVEALED</span>
            )}
            {!timerExpired && !timerCancelled && (
              <span style={{
                fontSize:'clamp(9px,.9vw,11px)', color:'rgba(255,255,255,.35)', fontWeight:700,
                textTransform:'uppercase', letterSpacing:2,
                fontFamily:"'Barlow Condensed',sans-serif",
              }}>1 Answer = 50 pts</span>
            )}
          </div>
          <TimerBar
            timerSeconds={timerInfo.timerSeconds}
            startedAt={timerInfo.startedAt}
            onExpired={onExpired}
            cancelled={timerCancelled}
          />
        </>)}
      </div>
    </div>
  );
}





// ─── Classic TV Show Board ─────────────────────────────────────────────────────
// Horizontal stacked answer bars that mimic the GSN America Says game show set:
// red question banner, navy answer bars, gold rank circles, clean white typography.
function ClassicTVBoard({ answers, revealed, animKeys, question, currentQ, totalQuestions,
                          timerInfo, timerExpired, timerCancelled, onExpired }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', gap:'clamp(5px,.8vh,10px)' }}>

      {/* ── Question Banner (red, like the real show) ── */}
      <div style={{
        flexShrink: 0,
        borderRadius: 'clamp(10px,1.2vw,18px)',
        background: 'linear-gradient(135deg, #A80000 0%, #D40000 50%, #A80000 100%)',
        padding: 'clamp(10px,1.4vh,18px) clamp(16px,2.5vw,30px)',
        position: 'relative', overflow: 'hidden',
        boxShadow: '0 4px 28px rgba(180,0,0,.5), inset 0 1px 0 rgba(255,255,255,.18)',
        border: '2px solid rgba(255,255,255,.14)',
      }}>
        <div style={{ position:'absolute', top:0, left:0, right:0, height:'45%',
          background:'rgba(255,255,255,.1)', borderRadius:'inherit', pointerEvents:'none' }} />
        <div style={{ display:'flex', alignItems:'center', gap:'clamp(10px,1.4vw,20px)', position:'relative', zIndex:1 }}>
          <div style={{
            flexShrink: 0, background:'rgba(0,0,0,.28)', borderRadius:6, padding:'3px 10px',
            fontFamily:"'Bebas Neue', sans-serif",
            fontSize:'clamp(12px,1.6vw,32px)', letterSpacing:4, color:'rgba(255,255,255,.55)',
          }}>
            Q{(currentQ||0)+1}{totalQuestions > 1 ? ` / ${totalQuestions}` : ''}
          </div>
          <div style={{
            flex:1, fontFamily:"'Bebas Neue', 'Impact', sans-serif",
            fontSize:'clamp(18px,3.2vw,68px)', fontWeight:900,
            color:'#fff', letterSpacing:'0.04em', lineHeight:1.1,
            textShadow:'0 2px 0 rgba(0,0,0,.4)',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {question.prompt}
          </div>
        </div>
      </div>

      {/* ── Answer Rows ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', gap:'clamp(4px,.55vh,8px)', minHeight:0 }}>
        {answers.map((ans, idx) => {
          const revEntry   = revealed[idx];
          const isRevealed = !!revEntry;
          const byAdmin    = revEntry?.revealedByAdmin;
          const isAnim     = !!animKeys[idx];

          // Public surveys carry hint-only answers (no text) — revealed text
          // comes from the reveal entry written by the server on each answer.
          const answerText = revEntry?.answerText || ans.text || '';
          const hint = ans.hint || (ans.text || '').toUpperCase().split(' ').map(w => {
            const vis = Math.min(2, w.length);
            return w.slice(0, vis) + '_ '.repeat(w.length - vis).trimEnd();
          }).join('   ');

          const barBg = isRevealed
            ? (byAdmin ? 'linear-gradient(90deg,#7B4500,#C47800)' : 'linear-gradient(90deg,#003799,#0052DD)')
            : 'linear-gradient(90deg,#0b1845,#122060)';
          const barBorder = isRevealed
            ? (byAdmin ? 'rgba(255,179,0,.75)' : 'rgba(60,140,255,.75)')
            : 'rgba(28,68,170,.5)';
          const barGlow = isRevealed
            ? (byAdmin
                ? '0 0 28px rgba(255,130,0,.28), inset 0 1px 0 rgba(255,200,100,.18)'
                : '0 0 28px rgba(0,100,255,.25), inset 0 1px 0 rgba(100,180,255,.12)')
            : '0 2px 8px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.04)';

          return (
            <div key={idx} style={{
              flex:1, display:'flex', alignItems:'center',
              gap:'clamp(8px,1.2vw,16px)', minHeight:0,
              animation: isAnim ? 'classicReveal .5s cubic-bezier(.34,1.56,.64,1)' : 'none',
            }}>
              {/* Rank circle */}
              <div style={{
                width:'clamp(30px,4vw,74px)', height:'clamp(30px,4vw,74px)', flexShrink:0,
                borderRadius:'50%',
                background: isRevealed
                  ? (byAdmin ? 'linear-gradient(135deg,#FFB300,#FF6B00)' : 'linear-gradient(135deg,#0060E6,#003EB0)')
                  : 'linear-gradient(135deg,#1a2a6e,#0d1a4a)',
                border:`2px solid ${isRevealed ? (byAdmin ? '#FFB300' : '#4499FF') : 'rgba(60,100,200,.4)'}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontFamily:"'Bebas Neue', sans-serif",
                fontSize:'clamp(13px,2vw,40px)', fontWeight:900,
                color: isRevealed ? '#fff' : 'rgba(100,150,255,.45)',
                boxShadow: isRevealed
                  ? (byAdmin ? '0 0 18px rgba(255,140,0,.5)' : '0 0 18px rgba(0,100,255,.5)')
                  : 'none',
                transition:'all .4s ease',
              }}>{idx + 1}</div>

              {/* Answer bar */}
              <div style={{
                flex:1, height:'100%', position:'relative',
                borderRadius:'clamp(8px,1vw,14px)',
                background: barBg, border:`2px solid ${barBorder}`,
                boxShadow: barGlow,
                display:'flex', alignItems:'center',
                padding:'0 clamp(12px,2vw,24px)',
                overflow:'hidden',
                transition:'background .4s ease, border-color .4s ease, box-shadow .4s ease',
              }}>
                {/* Top shine */}
                <div style={{ position:'absolute', top:0, left:0, right:0, height:'38%',
                  background:'rgba(255,255,255,.07)',
                  borderRadius:'clamp(7px,.9vw,13px) clamp(7px,.9vw,13px) 0 0',
                  pointerEvents:'none' }} />
                {/* Shimmer on unrevealed */}
                {!isRevealed && (
                  <div style={{
                    position:'absolute', inset:0, pointerEvents:'none',
                    background:'linear-gradient(90deg,transparent 0%,rgba(255,255,255,.05) 50%,transparent 100%)',
                    backgroundSize:'200% 100%',
                    animation:'shimmer 3.5s ease infinite',
                  }} />
                )}
                <div style={{
                  fontSize:'clamp(15px,2.8vw,58px)', fontWeight:900,
                  fontFamily:"'Bebas Neue','Impact',sans-serif",
                  letterSpacing:'0.07em', lineHeight:1,
                  color: isRevealed ? '#fff' : 'rgba(100,155,255,.45)',
                  textShadow: isRevealed
                    ? '0 2px 0 rgba(0,0,0,.5), 0 0 20px rgba(255,255,255,.2)'
                    : 'none',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', width:'100%',
                  position:'relative', zIndex:1,
                  transition:'color .35s ease',
                }}>
                  {isRevealed ? answerText.toUpperCase() : hint}
                </div>
                {/* Player name badge */}
                {isRevealed && revEntry?.playerName && !byAdmin && (
                  <div style={{
                    flexShrink:0, marginLeft:'auto', paddingLeft:12,
                    fontSize:'clamp(9px,1vw,20px)', color:'rgba(150,210,255,.75)',
                    fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, fontWeight:700,
                    whiteSpace:'nowrap', position:'relative', zIndex:1,
                  }}>✓ {revEntry.playerName}</div>
                )}
              </div>

              {/* Points */}
              <div style={{
                width:'clamp(42px,6vw,104px)', textAlign:'center', flexShrink:0,
                fontFamily:"'Bebas Neue', sans-serif",
                fontSize:'clamp(13px,2vw,42px)', fontWeight:900, letterSpacing:1,
                color: isRevealed ? (byAdmin ? '#FFB300' : '#64AAFF') : 'rgba(255,255,255,.2)',
                textShadow: isRevealed
                  ? (byAdmin ? '0 0 14px rgba(255,179,0,.6)' : '0 0 14px rgba(100,170,255,.5)')
                  : 'none',
                transition:'color .4s ease',
              }}>{ans.points}</div>
            </div>
          );
        })}
      </div>

      {/* Timer */}
      {timerInfo.timerSeconds > 0 && (
        <div style={{ flexShrink:0, padding:'0 2px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
            <span style={{ fontSize:'clamp(9px,1vw,12px)', color:'rgba(255,255,255,.3)', fontWeight:700,
              textTransform:'uppercase', letterSpacing:2, fontFamily:"'Barlow Condensed',sans-serif" }}>
              1 Answer = 50 pts
            </span>
            {timerExpired && !timerCancelled && (
              <span style={{ fontSize:'clamp(9px,1vw,12px)', color:'#FF4B4B', fontWeight:700,
                textTransform:'uppercase', letterSpacing:2, fontFamily:"'Barlow Condensed',sans-serif",
                animation:'timerPulse .6s ease infinite' }}>
                ✕ TIME'S UP
              </span>
            )}
            {timerCancelled && (
              <span style={{ fontSize:'clamp(9px,1vw,12px)', color:'rgba(255,153,0,.8)', fontWeight:700,
                textTransform:'uppercase', letterSpacing:2, fontFamily:"'Barlow Condensed',sans-serif" }}>
                👁 REVEALED
              </span>
            )}
          </div>
          <TimerBar
            timerSeconds={timerInfo.timerSeconds}
            startedAt={timerInfo.startedAt}
            onExpired={onExpired}
            cancelled={timerCancelled}
          />
        </div>
      )}
    </div>
  );
}

// ─── Question Intro Screen ────────────────────────────────────────────────────
function QuestionIntroScreen({ question, questionNumber, totalQuestions, onDone, holdSeconds = 10 }) {
  const [phase, setPhase]       = useState('enter'); // enter | hold | exit
  const [countdown, setCountdown] = useState(holdSeconds);
  const [showShockwave, setShowShockwave] = useState(false);
  const timerRef  = useRef(null);
  // Store onDone in a ref so it never changes identity — prevents the effect
  // from restarting every time the parent re-renders (which was causing the loop).
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; });

  useEffect(() => {
    const enterDelay = setTimeout(() => {
      setPhase('hold');
      let remaining = holdSeconds;
      timerRef.current = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0) {
          clearInterval(timerRef.current);
          // Trigger shockwave burst before exit
          setShowShockwave(true);
          setTimeout(() => {
            setPhase('exit');
            setTimeout(() => onDoneRef.current?.(), 700);
          }, 200);
        }
      }, 1000);
    }, 800);
    return () => { clearTimeout(enterDelay); clearInterval(timerRef.current); };
  }, [holdSeconds]); // onDone intentionally omitted — stored in onDoneRef to prevent loop restarts

  const isExiting = phase === 'exit';

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 50,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0D0B2A 0%, #0A1A5C 40%, #0D2E8C 70%, #0A1A5C 100%)',
      backgroundSize: '400% 400%',
      animation: isExiting
        ? 'overlayExit 0.65s cubic-bezier(.4,0,.2,1) forwards'
        : phase === 'enter'
          ? 'overlayEnter 0.45s cubic-bezier(.34,1.2,.64,1) both'
          : 'introBgPulse 8s ease 0.45s infinite',
      overflow: 'hidden',
    }}>
      {/* Shockwave rings on transition */}
      {showShockwave && (
        <>
          <div style={{
            position:'absolute', top:'50%', left:'50%',
            width:'200px', height:'200px', borderRadius:'50%',
            border:'6px solid rgba(255,179,0,.9)',
            animation:'shockwaveRing 0.7s cubic-bezier(.2,0,.8,1) forwards',
            pointerEvents:'none',
          }} />
          <div style={{
            position:'absolute', top:'50%', left:'50%',
            width:'200px', height:'200px', borderRadius:'50%',
            border:'4px solid rgba(255,255,255,.7)',
            animation:'shockwaveRing 0.7s cubic-bezier(.2,0,.8,1) 0.08s forwards',
            pointerEvents:'none',
          }} />
          <div style={{
            position:'absolute', inset:0,
            background:'rgba(255,230,100,.12)',
            animation:'flashBurst 0.5s ease forwards',
            pointerEvents:'none',
          }} />
        </>
      )}

      {/* Background grid */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(255,255,255,.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.03) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
      }} />

      {/* Gold accent bars */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 6,
        background: 'linear-gradient(90deg, #FF8C00, #FFB300, #FFD700, #FFB300, #FF8C00)',
        backgroundSize: '400% 100%',
        animation: 'introBgPulse 3s ease infinite',
      }} />
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 6,
        background: 'linear-gradient(90deg, #FF8C00, #FFB300, #FFD700, #FFB300, #FF8C00)',
        backgroundSize: '400% 100%',
        animation: 'introBgPulse 3s ease infinite reverse',
      }} />

      {/* Radial spotlight */}
      <div style={{
        position:'absolute', inset:0,
        background:'radial-gradient(ellipse at 50% 50%, rgba(255,200,80,.06) 0%, transparent 65%)',
        pointerEvents:'none',
      }} />

      {/* Question number badge */}
      <div style={{
        marginBottom: 'clamp(20px,3vh,44px)',
        animation: 'introTextReveal 0.6s cubic-bezier(.34,1.56,.64,1) 0.1s both',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <div style={{ width:2, height:40, background:'linear-gradient(180deg, transparent, #FFB300, transparent)' }} />
        <span style={{ fontSize:'clamp(14px,1.9vw,32px)', fontWeight:700, letterSpacing:8,
          fontFamily:"'Barlow Condensed', sans-serif",
          color:'rgba(255,179,0,0.8)', textTransform:'uppercase' }}>
          Question {questionNumber}{totalQuestions > 1 ? ` of ${totalQuestions}` : ''}
        </span>
        <div style={{ width:2, height:40, background:'linear-gradient(180deg, transparent, #FFB300, transparent)' }} />
      </div>

      {/* Main question banner */}
      <div style={{
        width: '90%', maxWidth: 'min(1500px,90vw)',
        animation: 'introBannerSlide 0.7s cubic-bezier(.34,1.56,.64,1) 0.2s both',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute', inset: -4,
          background: 'linear-gradient(135deg, #FFB300, #FF8C00, #FFB300)',
          borderRadius: 14, padding: 4,
        }}>
          <div style={{ background: '#8B0000', borderRadius: 11, height: '100%' }} />
        </div>

        <div style={{
          position: 'relative', zIndex: 1,
          background: 'linear-gradient(135deg, #9B0000 0%, #CC0000 40%, #AA0000 100%)',
          borderRadius: 10,
          padding: 'clamp(28px,3vh,50px) clamp(44px,4vw,76px) clamp(32px,3.4vh,56px)',
          boxShadow: '0 0 60px rgba(200,0,0,0.4), inset 0 1px 0 rgba(255,255,255,.12)',
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '40%',
            background: 'rgba(255,255,255,.07)', borderRadius: '10px 10px 0 0',
            pointerEvents: 'none',
          }} />
          <div style={{
            fontSize: 'clamp(24px, 4.8vw, 92px)',
            fontWeight: 900,
            fontFamily: "'Bebas Neue', 'Impact', sans-serif",
            color: '#FFFFFF',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            lineHeight: 1.15,
            textShadow: '0 3px 0 rgba(0,0,0,0.5), 0 0 40px rgba(0,0,0,0.3)',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            animation: 'introWipe 0.8s cubic-bezier(.4,0,.2,1) 0.4s both',
            position: 'relative', zIndex: 1,
          }}>
            {question.prompt}
          </div>
        </div>
      </div>

      {/* Countdown ring */}
      <div style={{
        marginTop: 'clamp(28px, 4vh, 56px)',
        animation: 'introTextReveal 0.5s ease 0.9s both',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
      }}>
        <div style={{ position: 'relative', width: 'clamp(80px,10vw,120px)', height: 'clamp(80px,10vw,120px)' }}>
          <svg viewBox="0 0 80 80" style={{ position: 'absolute', inset: 0, width:'100%', height:'100%', transform: 'rotate(-90deg)' }}>
            <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,179,0,.15)" strokeWidth="5" />
            <circle
              cx="40" cy="40" r="34" fill="none"
              stroke="#FFB300" strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 34}`}
              strokeDashoffset={`${2 * Math.PI * 34 * (1 - countdown / holdSeconds)}`}
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 'clamp(28px,3.5vw,42px)', fontWeight: 900,
            fontFamily: "'Bebas Neue', sans-serif",
            color: countdown <= 3 ? '#FF4B4B' : '#FFB300',
            textShadow: countdown <= 3 ? '0 0 20px rgba(255,75,75,.7)' : '0 0 20px rgba(255,179,0,.6)',
            animation: countdown <= 3 ? 'timerPulse 0.5s ease infinite' : 'none',
          }}>
            {countdown}
          </div>
        </div>
        <div style={{
          fontSize: 'clamp(11px,1.2vw,15px)', letterSpacing: 5, color: 'rgba(255,255,255,.3)',
          fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
          textTransform: 'uppercase',
        }}>
          Get Ready
        </div>
      </div>
    </div>
  );
}


function CompletedScreen({ scores, teamScores, gameName, newGameCountdown, onExit }) {
  const winner = scores[0];
  const hasTeams = teamScores?.length > 0;
  const metals = ['#FFD700','#C8C8C8','#CD7F32'];
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', height:'100%', gap:hasTeams?14:20, animation:'fadeIn 1s ease',
      position:'relative', overflowY:'auto', padding:'8px 24px' }}>

      <div style={{ fontSize:hasTeams?'clamp(48px,6vw,110px)':'clamp(80px,9vw,180px)', filter:'drop-shadow(0 0 32px rgba(255,215,0,.8))',
        animation:'winnerPop 1s ease both' }}>🏆</div>

      <h1 style={{ fontSize:hasTeams?'clamp(34px,4vw,74px)':'clamp(52px,6vw,120px)', fontWeight:700, color:'#FFD700', textAlign:'center',
        fontFamily:"'Bebas Neue',sans-serif", textTransform:'uppercase', letterSpacing:4,
        textShadow:'0 0 40px rgba(255,215,0,.8)', animation:'winnerGlow 2s ease infinite' }}>
        Game Over!
      </h1>

      {/* Team standings (team mode only) */}
      {hasTeams && (
        <div style={{ width:'100%', maxWidth:'min(2400px,96vw)', animation:'fadeIn .8s .3s ease both' }}>
          <div style={{ textAlign:'center', fontSize:'clamp(10px,.9vw,16px)', letterSpacing:5, color:'rgba(255,255,255,.3)',
            fontFamily:"'Barlow Condensed',sans-serif", marginBottom:8, textTransform:'uppercase' }}>
            Team Standings
          </div>
          <div style={{ display:'flex', gap:'clamp(12px,1.2vw,24px)', justifyContent:'center', flexWrap:'wrap' }}>
            {teamScores.map((t,i) => (
              <div key={t.id} style={{ textAlign:'center', background:`${t.color}14`,
                borderRadius:16, padding:'clamp(16px,1.6vw,28px) clamp(28px,2.4vw,48px)',
                border:`2px solid ${t.color}${i===0?'88':'44'}`,
                animation:`fadeIn .6s ${.4+i*.12}s ease both`, minWidth:'clamp(120px,11vw,210px)' }}>
                <div style={{ fontSize:'clamp(20px,1.8vw,34px)', marginBottom:6 }}>{['🥇','🥈','🥉'][i]||`#${i+1}`}</div>
                <div style={{ width:10, height:10, borderRadius:'50%', background:t.color,
                  margin:'0 auto 8px', boxShadow:`0 0 12px ${t.color}` }} />
                <div style={{ fontSize:'clamp(20px,1.8vw,36px)', fontWeight:700, fontFamily:"'Bebas Neue',sans-serif",
                  color:i===0?'#FFD700':'#fff', letterSpacing:1 }}>{t.name}</div>
                <div style={{ fontSize:'clamp(28px,2.6vw,54px)', fontWeight:900, fontFamily:"'Bebas Neue',sans-serif",
                  color:t.color, textShadow:`0 0 16px ${t.color}` }}>{t.score}</div>
                <div style={{ fontSize:'clamp(10px,.9vw,16px)', color:'rgba(255,255,255,.3)',
                  fontFamily:"'Barlow Condensed',sans-serif" }}>{t.playerCount} player{t.playerCount!==1?'s':''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {winner && (
        <div style={{ textAlign:'center', background:'rgba(0,0,0,.45)',
          borderRadius:20, padding:hasTeams?'18px 40px':'28px 56px', border:'2px solid rgba(255,215,0,.35)',
          boxShadow:'0 0 60px rgba(255,215,0,.15)', animation:'fadeIn .8s .4s ease both' }}>
          <div style={{ color:'rgba(255,255,255,.4)', fontSize:'clamp(12px,1vw,20px)', marginBottom:10,
            textTransform:'uppercase', letterSpacing:5,
            fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700 }}>
            {hasTeams ? '🥇 Top Player' : '🥇 Winner'}
          </div>
          <div style={{ fontSize:hasTeams?'clamp(40px,4vw,80px)':'clamp(56px,6vw,120px)', fontWeight:700, color:'#fff',
            fontFamily:"'Bebas Neue',sans-serif", letterSpacing:3,
            textShadow:'0 0 30px rgba(255,179,0,.5)' }}>{winner.name}</div>
          <div style={{ color:'#FFB300', fontSize:hasTeams?'clamp(28px,2.6vw,52px)':'clamp(36px,3.4vw,72px)', fontWeight:700, marginTop:6,
            fontFamily:"'Bebas Neue',sans-serif",
            textShadow:'0 0 20px rgba(255,179,0,.7)' }}>{winner.score} pts</div>
        </div>
      )}

      {scores.length > 1 && (
        <div style={{ display:'flex', gap:'clamp(12px,1.2vw,24px)', flexWrap:'wrap', justifyContent:'center',
          maxWidth:'min(1100px,92vw)', animation:'fadeIn 1s .8s ease both' }}>
          {scores.slice(1, 4).map((p, i) => (
            <div key={p.jid || i} style={{ textAlign:'center',
              background:'rgba(255,255,255,.05)', borderRadius:12,
              padding:'clamp(12px,1.2vw,22px) clamp(20px,2vw,40px)', border:'1px solid rgba(255,255,255,.1)' }}>
              <div style={{ fontSize:'clamp(18px,1.6vw,32px)', marginBottom:4 }}>{['🥈','🥉','4️⃣'][i]}</div>
              <div style={{ color:'rgba(255,255,255,.8)', fontSize:'clamp(14px,1.2vw,26px)', fontWeight:700,
                fontFamily:"'Barlow Condensed',sans-serif" }}>{p.name}</div>
              <div style={{ color:'rgba(255,179,0,.7)', fontSize:'clamp(13px,1.1vw,22px)',
                fontFamily:"'Bebas Neue',sans-serif" }}>{p.score} pts</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:12,
        marginTop:8, animation:'fadeIn 1s 1.2s ease both' }}>
        {newGameCountdown !== null && (
          <div style={{ color:'rgba(255,255,255,.5)', fontSize:13,
            fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:3 }}>
            New game detected — returning to lobby in{' '}
            <span style={{ color:'#FFB300', fontWeight:700 }}>{newGameCountdown}s</span>
          </div>
        )}
        {onExit && (
          <button onClick={onExit} style={{
            background:'rgba(255,179,0,.12)', border:'1px solid rgba(255,179,0,.4)',
            color:'#FFB300', borderRadius:10, padding:'10px 32px', cursor:'pointer',
            fontSize:13, fontFamily:"'Barlow Condensed',sans-serif",
            fontWeight:700, letterSpacing:3, transition:'all .2s',
          }}>
            ▶ NEW GAME
          </button>
        )}
      </div>
    </div>
  );
}


// ─── Audio Unlock Button ──────────────────────────────────────────────────────
function AudioUnlockBtn({ onUnlock }) {
  return (
    <button
      onClick={onUnlock}
      style={{
        position:'fixed', bottom:18, right:18, zIndex:9999,
        background:'rgba(255,179,0,.14)', border:'2px solid rgba(255,179,0,.5)',
        borderRadius:40, padding:'9px 20px', cursor:'pointer',
        display:'flex', alignItems:'center', gap:8,
        backdropFilter:'blur(16px)',
        animation:'liveFlash 2s ease infinite',
        boxShadow:'0 0 24px rgba(255,140,0,.3)',
      }}
    >
      <span style={{ fontSize:16 }}>🔇</span>
      <span style={{ fontSize:10, fontWeight:700, color:'#FFB300',
        fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2 }}>
        TAP TO ENABLE SOUND
      </span>
    </button>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function AmericaSaysBoard({ gameId: propGameId, onExit, onAutoLaunch, tvMode = false }) {
  const params  = new URLSearchParams(window.location.search);
  // Accept gameId from prop (SPA routing) or URL param (direct link / legacy)
  const gameId  = propGameId || params.get('game');
  const [view, setView] = useState(tvMode ? 'display' : (params.get('view') || 'display'));

  // All sound effects — HTMLAudioElement based, no Web Audio API complexity
  const sounds = useSound();
  const [audioUnlocked, setAudioUnlocked] = useState(LITE);

  // Auto-mark audio as unlocked on first user gesture
  useEffect(() => {
    const mark = () => setAudioUnlocked(true);
    document.addEventListener('click', mark, { once: true });
    document.addEventListener('keydown', mark, { once: true });
    document.addEventListener('touchstart', mark, { once: true });
    return () => {
      document.removeEventListener('click', mark);
      document.removeEventListener('keydown', mark);
      document.removeEventListener('touchstart', mark);
    };
  }, []);

  // Keep the URL bar in sync without triggering a navigation/reload
  const handleViewChange = useCallback((v) => {
    setView(v);
    const u = new URL(window.location.href);
    u.searchParams.set('view', v);
    window.history.replaceState(null, '', u.toString());
  }, []);

  const [game,         setGame]         = useState(null);
  const [survey,       setSurvey]       = useState(null);
  const [scores,       setScores]       = useState([]);
  const [teamScores,   setTeamScores]   = useState([]);
  const [fireworks,    setFireworks]    = useState(false);
  const [animKeys,     setAnimKeys]     = useState({});
  const [timerInfo,    setTimerInfo]    = useState({ timerSeconds:0, startedAt:null });
  const timerInfoRef                    = useRef({ timerSeconds:0, startedAt:null });
  const [timerExpired,    setTimerExpired]    = useState(false);
  const [timerCancelled,  setTimerCancelled]  = useState(false);
  const timerCancelledRef                     = useRef(false);
  // Seconds until the server auto-advances to the next question (null = not queued)
  const [advanceIn,       setAdvanceIn]       = useState(null);
  const advanceTickRef                        = useRef(null);
  const [lbVisible,    setLbVisible]    = useState(false);
  const [popWinner,    setPopWinner]    = useState(false);
  // On the TV display default to the Classic TV Show view (the layout toggle is a
  // web button the D-pad can't reach); browser/admin still starts on grid + can cycle.
  const [layout,       setLayout]       = useState(tvMode ? 'classic' : 'grid'); // 'grid' | 'scatter' | 'classic'
  // Question intro screen state
  const [showIntro,    setShowIntro]    = useState(false);
  const [introData,    setIntroData]    = useState(null);
  const holdSecondsRef                  = useRef(10);

  // Background music — start when game goes active, stop when completed.
  // useSound() handles cleanup on unmount; no extra effect needed for that.
  const bgMusicStarted = useRef(false);
  // Debounce admin reveal sound: only play once per batch of answer-revealed events
  const adminRevealFiredRef = useRef(false);
  const adminRevealTimerRef = useRef(null);

  // Tracks whether the initial loadGame() has fully resolved (both game + survey
  // fetched). Ably event handlers check this to decide whether to apply
  // incremental updates or re-fetch the full state.
  const gameLoadedRef = useRef(false);

  useEffect(() => {
    if (game?.status === 'active' && !bgMusicStarted.current) {
      bgMusicStarted.current = true;
      sounds.startMusic();
    }
    if (game?.status === 'completed') {
      sounds.stopMusic();
    }
  }, [game?.status]); // intentional: only re-run when game status changes

  const loadGame = useCallback(async () => {
    if (!gameId) return;
    gameLoadedRef.current = false; // will be set true once both fetches complete
    try {
      const g = await fetch(`${API}/americasays/${gameId}`).then(r=>r.json());
      setGame(g);
      setScores(Object.values(g.scores||{}).sort((a,b)=>b.score-a.score));
      if (g.teams?.length > 0) {
        const tm = {};
        g.teams.forEach(t => { tm[t.id] = { ...t, score: 0, playerCount: 0 }; });
        Object.values(g.scores||{}).forEach(s => {
          if (s.teamId && tm[s.teamId]) { tm[s.teamId].score += (s.score||0); tm[s.teamId].playerCount++; }
        });
        setTeamScores(Object.values(tm).sort((a,b) => b.score - a.score));
      } else {
        setTeamScores([]);
      }
      holdSecondsRef.current = Number(g.questionHoldSeconds) || 10;

      let s = null;
      if (g.surveyId) {
        s = await fetch(`${API}/surveys/${g.surveyId}`).then(r=>r.json());
        setSurvey(s);
      }

      // Mark that the initial load has completed — Ably handlers can now
      // apply incremental updates safely instead of re-fetching.
      gameLoadedRef.current = true;

      // ── Mid-game join recovery ────────────────────────────────────────────────
      // If the TV screen is opened while a game is already active, the
      // `question-changed` event that arms the timer has already fired and will
      // never replay. Reconstruct the timer state from the persisted
      // `questionStartedAt` field so the board shows the live game immediately
      // instead of staying on the waiting screen.
      if (g.status === 'active' && g.currentQ >= 0 && g.questionStartedAt) {
        const q = s?.questions?.[g.currentQ];
        const timerSeconds = q?.timerSeconds || g.defaultTimerSeconds || 30;

        // questionStartedAt is set to Date.now() + holdSeconds (intro duration).
        // The timer starts counting from that moment. If already elapsed, the bar
        // shows the correct remaining time or expired state immediately.
        const alreadyElapsed = (Date.now() - g.questionStartedAt) / 1000;
        const isExpired = alreadyElapsed >= timerSeconds;

        if (timerSeconds > 0) {
          const armed = { timerSeconds, startedAt: g.questionStartedAt };
          timerInfoRef.current = armed;
          setTimerInfo(armed);
          if (isExpired) setTimerExpired(true);
        }

        // Skip the intro overlay — joining an in-progress question, not a new one.
        setShowIntro(false);
      }
    } catch(e) { console.error('Failed to load game', e); }
  }, [gameId]);

  // Stable ref so Ably callbacks can call loadGame without stale closure issues
  const loadGameRef = useRef(loadGame);
  useEffect(() => { loadGameRef.current = loadGame; }, [loadGame]);

  useEffect(() => { loadGame(); }, [loadGame]);

  useEffect(() => {
    const show = () => { setLbVisible(true); setTimeout(()=>setLbVisible(false),9000); };
    const t  = setTimeout(show, 8000);
    const iv = setInterval(show, 35000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, []);

  // Track whether game has been loaded at least once; Ably events that arrive
  // before the first loadGame() resolves should re-trigger it rather than silently
  // dropping their updates (the stale-null-game problem that causes the TV screen
  // to stay on the waiting screen when launched mid-game).

  const stopAdvanceCountdown = useCallback(() => {
    if (advanceTickRef.current) { clearInterval(advanceTickRef.current); advanceTickRef.current = null; }
    setAdvanceIn(null);
  }, []);

  // Anchor on a deadline rather than decrementing, so a throttled background tab
  // (or a dropped frame on the FireTV) can't drift out of step with the server.
  const startAdvanceCountdown = useCallback((delaySeconds) => {
    if (advanceTickRef.current) clearInterval(advanceTickRef.current);
    const deadline = Date.now() + delaySeconds * 1000;
    const tick = () => {
      const left = Math.ceil((deadline - Date.now()) / 1000);
      if (left <= 0) { clearInterval(advanceTickRef.current); advanceTickRef.current = null; setAdvanceIn(0); return; }
      setAdvanceIn(left);
    };
    tick();
    advanceTickRef.current = setInterval(tick, 250);
  }, []);

  useEffect(() => () => { if (advanceTickRef.current) clearInterval(advanceTickRef.current); }, []);

  useAblyChannel(`americasays:game:${gameId}`, {
    'game-state': (msg) => {
      const { state } = msg.data;
      if (!gameLoadedRef.current) {
        // Arrived before initial load completed — re-fetch full state
        loadGameRef.current();
        return;
      }
      setGame(g => g ? { ...g, status: state } : g);
      if (state === 'completed') {
        stopAdvanceCountdown();
        setFireworks(true);
        setTimeout(() => setFireworks(false), 8000);
        sounds.gameOver();
        loadGameRef.current();
      }
    },
    'question-changed': (msg) => {
      const { questionIdx, prompt, timerSeconds, questionStartedAt, holdSeconds: evtHoldSeconds } = msg.data;
      sounds.resumeMusic();
      if (evtHoldSeconds) holdSecondsRef.current = Number(evtHoldSeconds);
      adminRevealFiredRef.current = false;
      if (adminRevealTimerRef.current) { clearTimeout(adminRevealTimerRef.current); adminRevealTimerRef.current = null; }

      if (!gameLoadedRef.current) {
        // Arrived before initial load — re-fetch so survey+game are both ready,
        // then the intro will show correctly (showIntro is already set below).
        loadGameRef.current();
      }

      stopAdvanceCountdown();
      setIntroData({ questionIdx, prompt, timerSeconds, questionStartedAt, holdSeconds: holdSecondsRef.current });
      setShowIntro(true);
      setAnimKeys({});
      setTimerExpired(false);
      setTimerCancelled(false);
      timerCancelledRef.current = false;
      setGame(g => g ? { ...g, currentQ: questionIdx } : g);
      timerInfoRef.current = { timerSeconds:0, startedAt:null };
      setTimerInfo({ timerSeconds:0, startedAt:null });
    },
    'answer-revealed': (msg) => {
      const { answerIdx, answerText, answerPoints, playerName, playerInitials, teamColor, scores:newScores, teamScores:newTeamScores, questionIdx, revealedByAdmin } = msg.data;
      if (!revealedByAdmin) {
        sounds.correct();
      } else {
        // Admin reveals fire one event per answer in rapid succession.
        // Only trigger the sound + BG-music fade once per batch using a
        // short debounce window — subsequent events in the same batch are silent.
        if (!adminRevealFiredRef.current) {
          adminRevealFiredRef.current = true;
          sounds.adminReveal();
        }
        // Reset the flag after the batch window so the next admin reveal works
        if (adminRevealTimerRef.current) clearTimeout(adminRevealTimerRef.current);
        adminRevealTimerRef.current = setTimeout(() => {
          adminRevealFiredRef.current = false;
          adminRevealTimerRef.current = null;
        }, 800);
      }
      if (newScores && newScores.length > 0) {
        setPopWinner(true);
        setTimeout(() => setPopWinner(false), 1400);
      }
      if (!gameLoadedRef.current) {
        // Arrived before initial load — just re-fetch so we get the full state
        loadGameRef.current();
        return;
      }
      setGame(g => {
        if (!g) return g;
        const revealed = { ...(g.revealed||{}) };
        revealed[questionIdx] = {
          ...(revealed[questionIdx]||{}),
          [answerIdx]: { playerName, playerInitials, teamColor, revealedByAdmin, answerText, answerPoints, ts:Date.now() },
        };
        return { ...g, revealed };
      });
      setScores(newScores||[]);
      if (newTeamScores?.length > 0) setTeamScores(newTeamScores);
      setAnimKeys(prev => ({ ...prev, [answerIdx]:true }));
      setTimeout(()=>setAnimKeys(prev => ({ ...prev, [answerIdx]:false })),1800);
    },
    'timer-cancelled': () => {
      setTimerCancelled(true);
      timerCancelledRef.current = true;
      setTimerExpired(false);
    },
    'auto-advance-scheduled': (msg) => {
      const { delaySeconds } = msg.data || {};
      if (delaySeconds > 0) startAdvanceCountdown(Number(delaySeconds));
    },
    'question-complete': (msg) => {
      const isAdminReveal = msg?.data?.revealedByAdmin === true;
      // Admin-triggered reveals: play sound only, no celebratory fireworks
      if (isAdminReveal) {
        sounds.questionComplete();
        return;
      }
      const info = timerInfoRef.current;
      // The clock is dead (players swept the board) — celebrate now rather than
      // waiting out a countdown nobody can answer into.
      if (timerCancelledRef.current) {
        sounds.questionComplete();
        setFireworks(true);
        setTimeout(() => setFireworks(false), 4000);
      } else if (info.startedAt && info.timerSeconds) {
        const elapsed   = (Date.now() - info.startedAt) / 1000;
        const remaining = Math.max(0, info.timerSeconds - elapsed);
        setTimeout(() => {
          setTimerExpired(true);
          sounds.questionComplete();
          setFireworks(true);
          setTimeout(() => setFireworks(false), 4000);
        }, remaining * 1000);
      } else {
        sounds.questionComplete();
        setFireworks(true);
        setTimeout(() => setFireworks(false), 4000);
      }
    },
  });

  // Listen on the admin channel for a new America Says game being created.
  // When one arrives (and this board is on the completed screen), auto-transition
  // directly to that new game without going through GameEntry — this lets an
  // unattended large-screen display keep running with no manual interaction.
  const [newGameCountdown, setNewGameCountdown] = useState(null);
  const countdownRef = useRef(null);
  const pendingNewGame = useRef(null); // { gameId, gameType }

  useAblyChannel('crossword:admin', {
    'americasays-deleted': (msg) => {
      if (msg.data?.gameId !== gameId) return;
      // Game was deleted — exit gracefully after a brief notification
      setGame(g => g ? { ...g, status: 'deleted' } : g);
      if (onExit) setTimeout(onExit, 3000);
    },
    'americasays-created': (msg) => {
      const { gameId: newId } = msg.data || {};
      // Only act if we're on the completed screen
      setGame(g => {
        if (g?.status !== 'completed') return g;

        // Store the new game info so we can launch it when countdown ends
        pendingNewGame.current = { gameId: newId, gameType: 'americasays' };

        let secs = 10;
        setNewGameCountdown(secs);
        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
          secs -= 1;
          if (secs <= 0) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            setNewGameCountdown(null);
            const target = pendingNewGame.current;
            pendingNewGame.current = null;
            if (target && onAutoLaunch) {
              // Direct launch: no trip through GameEntry
              onAutoLaunch(target);
            } else if (onExit) {
              // Fallback: go to entry if no auto-launch handler
              onExit();
            }
          } else {
            setNewGameCountdown(secs);
          }
        }, 1000);
        return g;
      });
    },
  });

  // Clean up countdown interval on unmount
  useEffect(() => () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (adminRevealTimerRef.current) clearTimeout(adminRevealTimerRef.current);
    pendingNewGame.current = null;
  }, []);

  if (!game) {
    return (
      <div style={{ background:'#0a0818', height:'100dvh', minHeight:'100vh', display:'flex',
        alignItems:'center', justifyContent:'center' }}>
        <style>{STYLES}</style>
        <AnimatedBackground />
        <div style={{ textAlign:'center', animation:'fadeIn 1s ease', position:'relative', zIndex:10 }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🎯</div>
          <p style={{ color:'rgba(255,255,255,.3)', fontSize:18,
            fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:4, fontWeight:700 }}>
            {gameId ? 'LOADING GAME…' : 'NO GAME ID SPECIFIED'}
          </p>
        </div>
      </div>
    );
  }

  const { status, currentQ, revealed={}, name:gameName } = game;
  const question      = survey?.questions?.[currentQ];
  const qRevealed     = revealed[currentQ] || {};
  const totalAnswers  = question?.answers?.length || 0;
  const revealedCount = Object.keys(qRevealed).length;
  const surveyName    = survey?.name || gameName;

  return (
    <div style={{
      height:'100dvh', minHeight:'100vh', display:'flex', flexDirection:'column', overflow:'hidden',
      background:'transparent',
      fontFamily:"'Barlow',sans-serif", color:'#fff',
      paddingTop:'env(safe-area-inset-top)',
      paddingBottom:'env(safe-area-inset-bottom)',
      paddingLeft:'env(safe-area-inset-left)',
      paddingRight:'env(safe-area-inset-right)',
    }}>
      <style>{`
        ${STYLES}
        @keyframes introWipe {
          0%{clip-path:inset(0 100% 0 0);opacity:0}
          100%{clip-path:inset(0 0% 0 0);opacity:1}
        }
      `}</style>
      <Fireworks active={fireworks} />
      {layout === 'classic' ? <StudioBackground /> : <AnimatedBackground />}

      {!tvMode && (
        <NavBar gameId={gameId} view={view} onViewChange={handleViewChange} surveyName={surveyName} status={status} />
      )}
      {tvMode && (
        <div style={{ position:'relative', zIndex:100, height:'clamp(46px,5vh,62px)', padding:'0 clamp(16px,2vw,28px)',
          display:'flex', alignItems:'center', gap:12,
          background:'linear-gradient(180deg, rgba(8,6,20,.98), rgba(10,8,24,.96))',
          borderBottom:'1px solid rgba(255,179,0,.12)', flexShrink:0 }}>
          <div style={{ fontSize:'clamp(15px,1.7vw,24px)', fontWeight:700, fontFamily:"'Barlow Condensed',sans-serif",
            color:'#fff', letterSpacing:3, textTransform:'uppercase',
            textShadow:'0 0 24px rgba(255,179,0,.5)', flex:1,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {surveyName || 'America Says'}
          </div>
          {status === 'active' && (
            <div style={{ display:'flex', alignItems:'center', gap:6,
              background:'rgba(255,75,75,.1)', border:'1px solid rgba(255,75,75,.35)',
              borderRadius:20, padding:'4px 14px' }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:'#FF4B4B',
                animation:'liveFlash .9s ease infinite' }} />
              <span style={{ fontSize:'clamp(10px,1vw,14px)', fontWeight:700, color:'#FF4B4B',
                fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:3 }}>LIVE</span>
            </div>
          )}
        </div>
      )}

      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden',
        position:'relative', zIndex:5 }}>

        {!tvMode && view === 'scores' && <ScoresView scores={scores} />}
        {!tvMode && view === 'join'   && <JoinView gameId={gameId} surveyName={surveyName} scores={scores} />}

        {(view === 'display' || tvMode) && (
          <div style={{ flex:1, display:'flex', flexDirection:'column',
            overflowX:'hidden',
            overflowY: (status === 'waiting' || status === 'completed') ? 'auto' : 'hidden',
            padding:'12px 14px', gap:10, position:'relative' }}>

            {status === 'waiting'   && <WaitingScreen gameId={gameId} gameName={gameName} game={game} />}
            {status === 'completed' && <CompletedScreen scores={scores} teamScores={teamScores} gameName={gameName} newGameCountdown={newGameCountdown} onExit={onExit} />}
            {status === 'deleted'   && (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                height:'100%', gap:16, animation:'fadeIn .6s ease' }}>
                <span style={{ fontSize:64 }}>🗑️</span>
                <div style={{ fontSize:28, fontWeight:700, color:'rgba(255,75,75,.7)',
                  fontFamily:"'Bebas Neue',sans-serif", letterSpacing:4 }}>GAME REMOVED</div>
                <div style={{ fontSize:14, color:'rgba(255,255,255,.3)',
                  fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:3 }}>Returning to lobby…</div>
              </div>
            )}

            {status === 'active' && !question && currentQ < 0 && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%' }}>
                <p style={{ color:'rgba(255,255,255,.3)', fontSize:20,
                  fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, fontWeight:600 }}>
                  Waiting for host to start the first question…
                </p>
              </div>
            )}

            {status === 'active' && question && (<>

              {/* Question Intro Overlay with enhanced transition */}
              {showIntro && introData && (
                <QuestionIntroScreen
                  key={`intro-${introData.questionIdx}`}
                  question={{ prompt: introData.prompt || question.prompt }}
                  questionNumber={(introData.questionIdx || 0) + 1}
                  totalQuestions={survey?.questions?.length || 1}
                  holdSeconds={holdSecondsRef.current}
                  onDone={() => {
                    // Arm the timer NOW — the game board is about to appear.
                    // Using Date.now() here means the countdown starts fresh
                    // the moment the overlay finishes, not when the event fired.
                    if (introData.timerSeconds) {
                      const armed = { timerSeconds: introData.timerSeconds, startedAt: Date.now() };
                      timerInfoRef.current = armed;
                      setTimerInfo(armed);
                    }
                    setShowIntro(false);
                  }}
                />
              )}

              {/* Game board — hidden while intro overlay is active, then reveals */}
              <div style={{
                display:'flex', flexDirection:'column', flex:1, gap:10,
                visibility: showIntro ? 'hidden' : 'visible',
                animation: !showIntro ? 'gameReveal 0.8s cubic-bezier(.34,1.4,.64,1) both' : 'none',
              }}>

                {layout === 'grid' ? (<>

                {/* Question Banner */}
                <div style={{ display:'flex', alignItems:'stretch', gap:0, flexShrink:0,
                  position:'relative', zIndex:5 }}>
                  <div style={{
                    width:68, minHeight:68, borderRadius:'16px 0 0 16px',
                    background:'linear-gradient(135deg, #FF8C00 0%, #FF6B00 100%)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontFamily:"'Bebas Neue',sans-serif",
                    fontSize:40, fontWeight:900, color:'#fff', flexShrink:0,
                    boxShadow:'inset 0 -3px 0 rgba(0,0,0,.3), 0 4px 20px rgba(255,107,0,.5)',
                    zIndex:6,
                  }}>
                    {(currentQ||0)+1}
                  </div>
                  <div style={{
                    flex:1, minHeight:68,
                    background:'linear-gradient(135deg, #2D1F6E 0%, #3730A3 50%, #4338CA 100%)',
                    borderRadius:'0 16px 16px 0',
                    padding:'0 24px 0 20px',
                    boxShadow:'0 4px 28px rgba(67,56,202,.6)',
                    position:'relative', overflow:'hidden',
                    display:'flex', alignItems:'center',
                  }}>
                    <div style={{ position:'absolute', top:0, left:0, right:0, height:'45%',
                      background:'rgba(255,255,255,.07)', borderRadius:'0 16px 0 0', pointerEvents:'none' }} />
                    <div style={{ position:'absolute', left:0, right:0, height:1,
                      background:'linear-gradient(90deg,transparent,rgba(255,255,255,.2),transparent)',
                      animation:'scanline 3s linear infinite', pointerEvents:'none' }} />
                    <div style={{
                      fontSize:'clamp(16px,2.6vw,52px)', fontWeight:700,
                      fontFamily:"'Barlow Condensed',sans-serif",
                      fontStyle:'italic', textTransform:'uppercase', letterSpacing:2,
                      color:'#fff', textShadow:'0 2px 8px rgba(0,0,0,.5)',
                      lineHeight:1.2, position:'relative', zIndex:1,
                    }}>
                      {question.prompt}
                    </div>
                    {survey?.questions?.length > 1 && (
                      <div style={{
                        position:'absolute', right:14, top:'50%', transform:'translateY(-50%)',
                        fontSize:10, color:'rgba(255,255,255,.35)',
                        fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2, fontWeight:700,
                      }}>
                        {(currentQ||0)+1}/{survey.questions.length}
                      </div>
                    )}
                  </div>
                </div>

                {/* Play Field */}
                <div style={{ flex:1, position:'relative', minHeight:0, overflow:'hidden' }}>

                  {/* Ambient glow background — subtle depth without clipping grid */}
                  <div style={{
                    position:'absolute', inset:0, borderRadius:20,
                    background:`
                      radial-gradient(ellipse at 30% 25%, rgba(0,180,255,.1) 0%, transparent 50%),
                      radial-gradient(ellipse at 70% 75%, rgba(80,40,200,.12) 0%, transparent 50%),
                      rgba(0,0,0,.18)
                    `,
                    animation:'circleBreath 10s ease infinite',
                    pointerEvents:'none',
                  }} />

                  {/* Answer grid */}
                  <div style={{ position:'absolute', inset:0 }}>
                    <AnswerGrid answers={question.answers} revealed={qRevealed} animKeys={animKeys} />
                  </div>

                  {/* Progress counter — floats above grid at bottom-center */}
                  <div style={{
                    position:'absolute', bottom:'clamp(6px,1vh,12px)', left:'50%',
                    transform:'translateX(-50%)',
                    display:'flex', alignItems:'center', gap:8,
                    background:'rgba(0,0,0,.45)', borderRadius:99,
                    padding:'4px 14px', backdropFilter:'blur(12px)', zIndex:10,
                    border:'1px solid rgba(255,255,255,.07)',
                    whiteSpace:'nowrap',
                  }}>
                    {question.answers.map((_,i) => (
                      <div key={i} style={{
                        width: qRevealed[i] ? 16 : 7, height:7, borderRadius:99,
                        background: qRevealed[i]
                          ? (qRevealed[i].revealedByAdmin ? 'rgba(255,179,0,.8)' : '#00E676')
                          : 'rgba(255,255,255,.25)',
                        transition:'all .4s ease',
                        boxShadow: qRevealed[i]
                          ? (qRevealed[i].revealedByAdmin
                              ? '0 0 6px rgba(255,140,0,.6)'
                              : '0 0 6px #00E676')
                          : 'none',
                        flexShrink: 0,
                      }} />
                    ))}
                    <span style={{ fontSize:'clamp(10px,1.1vw,13px)', color:'rgba(255,255,255,.6)',
                      fontWeight:700, marginLeft:4,
                      fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:1 }}>
                      {revealedCount}/{totalAnswers}
                    </span>
                  </div>
                </div>

                {/* Timer Bar */}
                {timerInfo.timerSeconds > 0 && (
                  <div style={{ flexShrink:0 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                      <span style={{ fontSize:10, color:'rgba(255,255,255,.3)', fontWeight:700,
                        textTransform:'uppercase', letterSpacing:2,
                        fontFamily:"'Barlow Condensed',sans-serif" }}>
                        1 Answer = 50 pts
                      </span>
                      {timerExpired && !timerCancelled && (
                        <span style={{ fontSize:10, color:'#FF4B4B', fontWeight:700,
                          textTransform:'uppercase', letterSpacing:2,
                          fontFamily:"'Barlow Condensed',sans-serif",
                          animation:'timerPulse .6s ease infinite' }}>
                          ✕ TIME'S UP
                        </span>
                      )}
                      {timerCancelled && (
                        <span style={{ fontSize:10, color:'rgba(255,153,0,.8)', fontWeight:700,
                          textTransform:'uppercase', letterSpacing:2,
                          fontFamily:"'Barlow Condensed',sans-serif" }}>
                          👁 REVEALED
                        </span>
                      )}
                    </div>
                    <TimerBar
                      timerSeconds={timerInfo.timerSeconds}
                      startedAt={timerInfo.startedAt}
                      onExpired={() => setTimerExpired(true)}
                      cancelled={timerCancelled}
                    />
                  </div>
                )}

                </>) : layout === 'scatter' ? (
                  <ScatterBoard
                    answers={question.answers}
                    revealed={qRevealed}
                    animKeys={animKeys}
                    question={question}
                    currentQ={currentQ || 0}
                    totalQuestions={survey?.questions?.length || 1}
                    timerInfo={timerInfo}
                    timerExpired={timerExpired}
                    timerCancelled={timerCancelled}
                    onExpired={() => setTimerExpired(true)}
                  />
                ) : (
                  <ClassicTVBoard
                    answers={question.answers}
                    revealed={qRevealed}
                    animKeys={animKeys}
                    question={question}
                    currentQ={currentQ || 0}
                    totalQuestions={survey?.questions?.length || 1}
                    timerInfo={timerInfo}
                    timerExpired={timerExpired}
                    timerCancelled={timerCancelled}
                    onExpired={() => setTimerExpired(true)}
                  />
                )}
              </div>
            </>)}
          </div>
        )}

        {/* Leaderboard Overlay — hidden on the completed screen, where the
            full-width podium already shows the team standings + top players
            (avoids duplication and frees the width for 4+ team cards). */}
        {(view === 'display' || tvMode) && status !== 'completed' && (
          <div style={{
            position:'fixed', right:18, top:70, zIndex:60,
            transform: lbVisible ? 'translateX(0)' : 'translateX(calc(100% + 40px))',
            transition: lbVisible
              ? 'transform .65s cubic-bezier(.34,1.56,.64,1), opacity .4s ease'
              : 'transform .5s cubic-bezier(.6,0,.4,1), opacity .4s ease',
            opacity: lbVisible ? 1 : 0,
            pointerEvents:'none',
          }}>
            <LeaderboardPanel scores={scores} teamScores={teamScores} popWinner={popWinner} />
          </div>
        )}
      </div>

      {/* Auto-advance countdown — server has queued the next question.
          Transform/opacity only: this sits on the FireTV's shared WebView. */}
      {(view === 'display' || tvMode) && advanceIn !== null && status === 'active' && !showIntro && (
        <div style={{
          position:'fixed', bottom:'clamp(14px,2.4vh,26px)', left:'50%',
          transform:'translateX(-50%)', zIndex:200,
          display:'flex', alignItems:'center', gap:12,
          background:'rgba(6,4,20,.9)',
          border:'1px solid rgba(255,179,0,.45)',
          borderRadius:99, padding:'8px 20px',
          boxShadow: LITE ? 'none' : '0 6px 26px rgba(0,0,0,.55)',
        }}>
          <span style={{
            fontSize:'clamp(10px,1.05vw,13px)', fontWeight:700, letterSpacing:2,
            fontFamily:"'Barlow Condensed',sans-serif", color:'rgba(255,179,0,.75)',
          }}>
            NEXT QUESTION IN
          </span>
          <span key={advanceIn} style={{
            minWidth:'1.6em', textAlign:'center',
            fontSize:'clamp(18px,1.9vw,26px)', fontWeight:800, lineHeight:1,
            fontFamily:"'Barlow Condensed',sans-serif", color:'#FFB300',
            display:'inline-block',
            animation: LITE ? 'none' : 'advanceTick .32s cubic-bezier(.34,1.56,.64,1)',
          }}>
            {Math.max(0, advanceIn)}
          </span>
        </div>
      )}

      {/* Layout toggle — floating button, accessible in both admin and TV mode */}
      {(view === 'display' || tvMode) && status === 'active' && (
        <button
          onClick={() => setLayout(l => l === 'grid' ? 'scatter' : l === 'scatter' ? 'classic' : 'grid')}
          title={
            layout === 'grid'    ? 'Switch to Scatter view' :
            layout === 'scatter' ? 'Switch to TV Show view' :
                                   'Switch to Grid view'
          }
          style={{
            position:'fixed', bottom:'clamp(14px,2vh,22px)', left:'clamp(12px,1.5vw,20px)',
            zIndex:200, display:'flex', alignItems:'center', gap:8,
            background:'rgba(6,4,20,.82)', backdropFilter:'blur(16px)',
            border:`1px solid ${layout === 'classic' ? 'rgba(0,120,255,.5)' : layout === 'scatter' ? 'rgba(180,60,255,.5)' : 'rgba(255,179,0,.3)'}`,
            borderRadius:10, padding:'6px 15px', cursor:'pointer',
            boxShadow:`0 4px 20px rgba(0,0,0,.5), 0 0 12px ${layout === 'classic' ? 'rgba(0,100,255,.15)' : layout === 'scatter' ? 'rgba(160,40,255,.15)' : 'rgba(255,140,0,.1)'}`,
            transition:'all .25s',
          }}
        >
          <span style={{ fontSize:14, lineHeight:1 }}>
            {layout === 'grid' ? '📺' : layout === 'scatter' ? '✦' : '⊞'}
          </span>
          <span style={{
            fontSize:'clamp(9px,1vw,11px)', fontWeight:700,
            color: layout === 'classic' ? 'rgba(100,180,255,.7)' : layout === 'scatter' ? 'rgba(200,120,255,.7)' : 'rgba(255,179,0,.6)',
            fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:2,
          }}>
            {layout === 'grid' ? 'TV VIEW' : layout === 'scatter' ? 'SCATTER' : 'GRID VIEW'}
          </span>
        </button>
      )}

      {!audioUnlocked && (
        <AudioUnlockBtn onUnlock={() => {
          sounds.forceUnlock();
          setAudioUnlocked(true);
        }} />
      )}
    </div>
  );
}
